import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import Pagination from '../components/Pagination';
import { Checkbox } from '../components/ui/checkbox';
import { useToast } from '../hooks/use-toast';
import SafetyAttestationDialog from '../components/SafetyAttestationDialog';
import OutsideGeofenceDialog from '../components/OutsideGeofenceDialog';
import QrScanDialog from '../components/QrScanDialog';
import BulkQrPrintDialog from '../components/BulkQrPrintDialog';
import { compressImage } from '../utils/compressImage';
import { uploadJobPhotoDirect } from '../utils/directUpload';
import { enqueuePhoto, flushPhotoQueue } from '../utils/photoOfflineQueue';
import BulkPhotoDownloadDialog from '../components/BulkPhotoDownloadDialog';
import { Briefcase, Play, Square, CheckCircle2, MapPin, User, Users, Plus, Trash2, UserCog, X, Camera, Image, Upload, Loader2, AlertCircle, Edit2, Trash, FileText, Download, Mail, Send, Clock, RefreshCw, ClipboardList, Activity, Pause, Tag, Search, StickyNote, Receipt as ReceiptIcon, ShieldCheck, Wrench, QrCode } from 'lucide-react';

import PageHeader from '../components/PageHeader';
import axios from 'axios';
import { COUNTRIES, getStatesForCountry, getPostalCodeLabel, getStateLabel } from '../data/countries';
import { formatTimeWithTimezone, formatDateTimeWithTimezone } from '../utils/timezone';
import Lightbox from '../components/Lightbox';
import PhotoMetaBar, { photoDisplayName } from '../components/PhotoMetaBar';
import { detectInvoiceCurrency } from '../utils/currency';
import { useTranslation } from 'react-i18next';
import JobDetailDialog from '../components/JobDetailDialog';
import AdminPhotoDialog from '../components/AdminPhotoDialog';
import { EmailJobPhotosDialog, EmailJobReportDialog } from '../components/JobEmailDialogs';
import QBTestSyncDialog from '../components/QBTestSyncDialog';
import InvoiceEditorDialog from '../components/InvoiceEditorDialog';
import LabelChip from '../components/LabelChip';
import ManageLabelsDialog from '../components/ManageLabelsDialog';
import DeleteWithReasonDialog from '../components/DeleteWithReasonDialog';
import QuickJobNoteDialog from '../components/QuickJobNoteDialog';
import AddJobDialog from '../components/jobs/AddJobDialog';
import EditJobDialog from '../components/jobs/EditJobDialog';
import JobRowCard from '../components/jobs/JobRowCard';
import TeamWorkingNow from '../components/jobs/TeamWorkingNow';
import AdminJobWorkbench from '../components/jobs/AdminJobWorkbench';
import InProgressJobsTab from '../components/jobs/InProgressJobsTab';
import PausedJobsTab from '../components/jobs/PausedJobsTab';
import ArchiveJobDialog from '../components/jobs/ArchiveJobDialog';
import SeasonalRevivePanel from '../components/jobs/SeasonalRevivePanel';
import { JobScopeToggle } from '../components/jobs/JobScopeToggle';
import { InvoiceNumberBadge } from '../components/jobs/InvoiceNumberBadge';
import { getJobScope, saveJobScope, hasJobScopePref } from '../utils/jobScope';
import { useCompanyDefaults, readCompanyDefaults } from '../hooks/useCompanyDefaults';
import { logger } from '../utils/logger';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

const JobsEnhanced = () => {
  const { user, token, isAdmin, isSupervisor, company } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [jobs, setJobs] = useState([]);
  // Worker/supervisor list scope ('mine' | 'team'): the worker's own choice on
  // this device wins, otherwise the company default (Settings → Company Defaults).
  const { defaults: companyDefaults, loaded: defaultsLoaded } = useCompanyDefaults(token);
  const [jobScope, setJobScope] = useState(() => getJobScope(readCompanyDefaults().jobs_default_scope));
  const jobScopeRef = useRef(jobScope);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [assignmentType, setAssignmentType] = useState('user'); // 'user' or 'team'
  const [isLoading, setIsLoading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showReassignDialog, setShowReassignDialog] = useState(false);
  const [reassignTargetType, setReassignTargetType] = useState('user');
  const [reassignSearch, setReassignSearch] = useState('');
  // Job Detail dialog state — opened via the "Open" button on each job row.
  // The dialog hosts Notes / Attachments / Activity tabs (June 2026).
  const [detailJob, setDetailJob] = useState(null);
  const [adminPhotoJob, setAdminPhotoJob] = useState(null); // admin drag-and-drop uploader (Nov 2026)
  const openAdminPhotos = (job) => setAdminPhotoJob(job);
  const [workbenchJob, setWorkbenchJob] = useState(null);
  // Per-job unread-notes counter for the small red pill on each job title.
  // Populated by GET /api/jobs/notes/unread-counts on mount; the dialog
  // clears the badge for that job once the Notes tab is opened.
  const [unreadNotes, setUnreadNotes] = useState({});
  // Company label catalog (Emergency, Night, Day rate, …) — fetched once
  // when the Jobs page mounts. Used to render colored chips on every job
  // card (Feb 2026). Indexed by id for O(1) lookup.
  const [companyLabels, setCompanyLabels] = useState([]);
  const labelsById = useMemo(() => {
    const m = {};
    for (const l of companyLabels) m[l.id] = l;
    return m;
  }, [companyLabels]);
  const [manageLabelsOpen, setManageLabelsOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showPhotoDialog, setShowPhotoDialog] = useState(false);
  // Safety attestation dialog state — opens when a worker clicks Start.
  const [safetyDialog, setSafetyDialog] = useState({ open: false });
  // Outside-geofence dialog state — opens when /start returns 403 with
  // the new Travel Time copy. Captures a reason + optional notes, then
  // retries the /start call with `outside_geofence_reason` in the body.
  const [geofenceDialog, setGeofenceDialog] = useState({ open: false });
  // Site QR scan dialog — opens before /start when the job has a posted
  // QR code. Scanning verifies location; skipping falls back to GPS.
  const [qrScanDialog, setQrScanDialog] = useState({ open: false });
  // Bulk Site QR poster printing — admin/supervisor picks active jobs.
  const [bulkQrOpen, setBulkQrOpen] = useState(false);
  const [showPhotoGallery, setShowPhotoGallery] = useState(false);
  // Lightbox state for the job photo gallery — null when closed.
  const [photoLightbox, setPhotoLightbox] = useState(null); // { images: [{src,name,id}], index: number }
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoUploadProgress, setPhotoUploadProgress] = useState({});
  const [bulkPhotoOpen, setBulkPhotoOpen] = useState(false);

  // Offline photo queue (Jun 2026): flush on mount, when connectivity returns,
  // and every 60s. Photos stashed while off-signal upload automatically.
  useEffect(() => {
    const flush = () => flushPhotoQueue({
      apiBase: API,
      token,
      onUploaded: (n) => {
        toast({
          title: 'Offline photos uploaded',
          description: `${n} queued photo${n === 1 ? '' : 's'} uploaded automatically`,
        });
        fetchJobs();
      },
      onDropped: (n) => {
        toast({
          title: 'Some queued photos were skipped',
          description: `${n} photo${n === 1 ? '' : 's'} could not be attached (job removed or photo limit reached)`,
          variant: 'destructive',
        });
      },
    });
    flush();
    window.addEventListener('online', flush);
    const iv = setInterval(flush, 60000);
    return () => { window.removeEventListener('online', flush); clearInterval(iv); };
  }, []); // mount-only: token is stable for the session
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [newJob, setNewJob] = useState({
    title: '',
    description: '',
    assigned_to: '',
    assigned_user_ids: [],
    assigned_team_id: '',
    customer_name: '',
    customer_email: '',
    customer_id: '',
    scheduled_start: '',
    scheduled_completion: '',
    service_rate: '',
    billable: true,
    email_on_start: false,
    email_on_complete: false,
    street_address: '',
    city: '',
    state_province: '',
    postal_code: '',
    country: 'US',
    photos_required: false,
    location_verification_required: false,
    geofence_radius_meters: '',
    is_extended: false,
    customer_po_number: '',
    work_order_number: '',
    project_id: '',
    project_number: '',
    project_name: '',
  });
  const [pdfFile, setPdfFile] = useState(null);
  const [newJobLabelIds, setNewJobLabelIds] = useState([]);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [locationMode, setLocationMode] = useState('address'); // 'address' or 'gps'
  const [gpsCoordinates, setGpsCoordinates] = useState({
    latitude: '',
    longitude: '',
    description: '' // Optional description like "Forestry Site North"
  });
  const [capturingGPS, setCapturingGPS] = useState(false);
  
  const [editJob, setEditJob] = useState(null);
  // Edit-dialog Project picker (Feb 2026) — local typeahead state. The
  // combobox supports both modes: pick from existing projects OR type a
  // freeform QBO project number. Searches /api/projects/search on every
  // keystroke (debounced) and lets admins pick a match; if they save
  // without picking, the typed string is stored as-is on the job.
  const [editProjResults, setEditProjResults] = useState([]);
  const [editProjOpen, setEditProjOpen] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  
  // New state for email features
  const [showEmailReportDialog, setShowEmailReportDialog] = useState(false);
  const [showEmailPhotosDialog, setShowEmailPhotosDialog] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [includePhotosInReport, setIncludePhotosInReport] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('todo');
  const [emptyNotice, setEmptyNotice] = useState(null);
  
  // Archive search and pagination
  const [archivedJobsList, setArchivedJobsList] = useState([]);
  const [archiveSearch, setArchiveSearch] = useState('');
  // Comprehensive search box on the Jobs page header (Feb 2026).
  // Filters the loaded `jobs` array client-side across all active tabs by:
  //   - assignee name (resolved from the company users list)
  //   - job_number, title (which is where "WO 295637" lives), description
  //   - customer name, address (street/city), status, paused_reason
  //   - label names (resolved via labelsById)
  // Workers with many jobs assigned can quickly narrow their list without
  // leaving the page; admins/supervisors can find a specific WO instantly.
  const [searchTerm, setSearchTerm] = useState('');
  const [activeLabelFilter, setActiveLabelFilter] = useState(null);
  const [labelSuggestions, setLabelSuggestions] = useState([]);
  const [archiveMatches, setArchiveMatches] = useState([]);
  const [archiveFilters, setArchiveFilters] = useState({
    start_date: '',
    end_date: '',
    worker_id: '',
    location: ''
  });
  const [showArchiveFilters, setShowArchiveFilters] = useState(false);
  const [archivePagination, setArchivePagination] = useState({
    page: 1,
    limit: 50,
    total_count: 0,
    total_pages: 0
  });
  const [loadingArchive, setLoadingArchive] = useState(false);
  
  // Employee statistics state
  const [employeeStats, setEmployeeStats] = useState({});
  const [loadingStats, setLoadingStats] = useState({});
  
  // Customers state
  const [customers, setCustomers] = useState([]);
  const [showAddCustomerInline, setShowAddCustomerInline] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [newCustomerEmail, setNewCustomerEmail] = useState('');
  const [newCustomerPhone, setNewCustomerPhone] = useState('');

  // QuickBooks invoice generation state
  const [qbConnected, setQbConnected] = useState(false);
  const [qbEnvironment, setQbEnvironment] = useState('production');
  const [invoiceJob, setInvoiceJob] = useState(null);          // job pending invoice confirmation
  const [pendingInvoiceJobId, setPendingInvoiceJobId] = useState(null); // from /jobs?invoice=<id>
  useEffect(() => {
    if (!pendingInvoiceJobId || !jobs.length) return;
    const j = jobs.find((x) => x.id === pendingInvoiceJobId);
    if (j) { setInvoiceJob(j); setPendingInvoiceJobId(null); }
  }, [pendingInvoiceJobId, jobs]);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [invoiceResult, setInvoiceResult] = useState(null);    // { invoice_id, amount, hours, rate, customer }
  // Bulk invoice generation
  const [bulkInvoiceOpen, setBulkInvoiceOpen] = useState(false);
  const [bulkInvoiceRunning, setBulkInvoiceRunning] = useState(false);
  const [bulkInvoiceResult, setBulkInvoiceResult] = useState(null);

  // Test-sync (dry-run preflight) modal
  const [testSyncJob, setTestSyncJob] = useState(null);

  // Native PDF invoice — email dialog state
  const [emailInvoiceJob, setEmailInvoiceJob] = useState(null);
  const [emailInvoiceTo, setEmailInvoiceTo] = useState('');
  const [emailInvoiceMessage, setEmailInvoiceMessage] = useState('');
  const [emailInvoiceSending, setEmailInvoiceSending] = useState(false);
  // Feb 2026: optional photo attachments on the invoice email so customers
  // get the bill and the proof-of-work in a single message. Default ON for
  // photo report (the prettier asset) when the job has any photos.
  const [includePhotoReport, setIncludePhotoReport] = useState(true);
  const [includePhotosZip, setIncludePhotosZip] = useState(false);

  const canManageJobs = isAdmin || isSupervisor;
  // Canonical "is this MY job to work?" rule (Jun 2026): direct assignee,
  // in the job's team-member snapshot, or currently on the assigned team.
  // Team members (including supervisors) always start/pause/complete team
  // jobs as THEMSELVES — never "on behalf of" a teammate.
  const canWorkJob = (job) => !!(job && (
    job.assigned_to === user.id ||
    (Array.isArray(job.assigned_team_member_ids) && job.assigned_team_member_ids.includes(user.id)) ||
    (job.assigned_team_id && user.team_id && job.assigned_team_id === user.team_id)
  ));
  // Generate Invoice / Bill out is ADMIN ONLY (Feb 2026). Workers + supervisors
  // enter materials on the job detail; admins finalize the invoice.
  const canBillOut = isAdmin;

  // Deep-link support: open the Job Detail dialog when the URL contains
  // `?job=<id>` (e.g. when navigating in from Global Search). We also handle
  // `?customer=<id>` from the customer search by filtering the list to that
  // customer. We resolve the job locally first, then fall back to a direct
  // `GET /api/jobs/{id}` so archived/out-of-tab jobs still resolve. After
  // opening, the query param is stripped from the URL so closing the dialog
  // doesn't re-open it on re-render.
  const location = useLocation();
  const navigate = useNavigate();
  const handledDeepLinkRef = useRef(null);

  // Honor ?status=... in the URL so dashboard tile clicks (e.g.
  // /jobs?status=in_progress) land the user on the right tab.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const s = (params.get('status') || params.get('tab') || '').toLowerCase();
    const map = {
      todo: 'todo',
      pending: 'todo',
      in_progress: 'inprogress',
      inprogress: 'inprogress',
      paused: 'paused',
      completed: 'completed',
      invoiced: 'invoiced',
      archived: 'archived',
      archive: 'archived',
    };
    const filter = (params.get('filter') || '').toLowerCase();
    if (filter === 'stale') { setActiveTab('paused'); setStaleOnly(true); }
    else if (map[s]) setActiveTab(map[s]);
    else if (filter === 'extended') setActiveTab('inprogress');
    else if (filter === 'overdue') setActiveTab('inprogress');
    // Dashboard zero-count deep-links: show an on-page notice that the
    // category is currently empty, then strip the params from the URL.
    // Draft-invoice notifications deep-link straight into the Invoice Editor.
    const invId = params.get('invoice');
    if (invId) setPendingInvoiceJobId(invId);
    if ((params.get('notice') || '').toLowerCase() === 'empty') {
      setEmptyNotice(params.get('label') || 'this category');
      const cleaned = new URLSearchParams(location.search);
      cleaned.delete('notice');
      cleaned.delete('label');
      const qs = cleaned.toString();
      navigate(`/jobs${qs ? `?${qs}` : ''}`, { replace: true });
    }
  }, [location.search, navigate]);

  useEffect(() => {
    if (!token) return;
    const params = new URLSearchParams(location.search);
    const targetJobId = params.get('job');
    if (!targetJobId) {
      handledDeepLinkRef.current = null;
      return;
    }
    // Avoid handling the same id twice (e.g., when query is cleared after open)
    if (handledDeepLinkRef.current === targetJobId) return;

    // Wait until at least one fetch attempt has completed (jobs is an array)
    if (!Array.isArray(jobs)) return;

    const local = jobs.find((j) => j.id === targetJobId)
      || archivedJobsList.find((j) => j.id === targetJobId);

    const openAndClean = (jobObj) => {
      if (!jobObj) return;
      handledDeepLinkRef.current = targetJobId;
      setDetailJob(jobObj);
      // Strip the ?job= param but preserve the rest of the URL state
      const cleaned = new URLSearchParams(location.search);
      cleaned.delete('job');
      const qs = cleaned.toString();
      navigate(`/jobs${qs ? `?${qs}` : ''}`, { replace: true });
    };

    if (local) {
      openAndClean(local);
      return;
    }

    // Fallback: fetch the job directly (handles archived jobs not in the
    // current page state, or jobs from other tabs)
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/jobs/${targetJobId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled && data && data.id) openAndClean(data);
      } catch (err) {
        if (!cancelled) {
          toast({
            title: 'Job not found',
            description: 'That work order is no longer available or you do not have access to it.',
            variant: 'destructive',
          });
          handledDeepLinkRef.current = targetJobId;
          const cleaned = new URLSearchParams(location.search);
          cleaned.delete('job');
          const qs = cleaned.toString();
          navigate(`/jobs${qs ? `?${qs}` : ''}`, { replace: true });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [location.search, jobs, archivedJobsList, token, navigate, toast]);

  useEffect(() => {
    if (token) {
      fetchJobs();
      fetchUnreadNotes();
      fetchLabels();
      if (canManageJobs) {
        fetchUsers();
        fetchTeams();
        fetchCustomers();
        // Check QuickBooks connection once for invoice button enablement
        axios.get(`${API}/qb/status`, { headers: { Authorization: `Bearer ${token}` } })
          .then((r) => {
            setQbConnected(!!r.data?.connected);
            setQbEnvironment(r.data?.environment || 'production');
          })
          .catch(() => setQbConnected(false));
      }
    }
  }, [token, canManageJobs]);

  // Helper function to calculate time since assignment
  const getTimeSinceAssignment = (createdAt) => {
    if (!createdAt) return 'N/A';
    
    const now = new Date();
    const created = new Date(createdAt);
    const diffMs = now - created;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) return `${diffDays}d ${diffHours % 24}h ago`;
    if (diffHours > 0) return `${diffHours}h ${diffMins % 60}m ago`;
    return `${diffMins}m ago`;
  };
  
  // Helper function to check if job is overdue (>30 minutes)
  const isJobOverdue = (createdAt) => {
    if (!createdAt) return false;
    const now = new Date();
    const created = new Date(createdAt);
    const diffMins = Math.floor((now - created) / 60000);
    return diffMins > 30;
  };
  
  // Helper function to calculate job duration
  const getJobDuration = (startedAt, completedAt) => {
    if (!startedAt) return 'N/A';
    
    const start = new Date(startedAt);
    const end = completedAt ? new Date(completedAt) : new Date();
    const diffMs = end - start;
    const diffSecs = Math.floor(diffMs / 1000);
    const hours = Math.floor(diffSecs / 3600);
    const minutes = Math.floor((diffSecs % 3600) / 60);
    
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  // ── Invoice helpers ────────────────────────────────────────────────
  // Use the company's customer-facing invoice currency (CAD/USD/...) so
  // amounts on cards, totals, and dialogs match what's billed. Falls back
  // to USD if the company doc hasn't been refreshed yet.
  const invoiceCurrency = (company?.invoice_currency || detectInvoiceCurrency(company?.country)).toUpperCase();
  const formatCurrency = (n) => {
    const num = Number(n) || 0;
    try {
      return num.toLocaleString(undefined, { style: 'currency', currency: invoiceCurrency, minimumFractionDigits: 2, maximumFractionDigits: 2 });
    } catch {
      return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
  };

  const formatHours = (h) => {
    const num = Number(h) || 0;
    // Use 4 decimals for very short jobs (< 0.1 hr ≈ 6 min) so test jobs don't display as "0.00"
    return num < 0.1 ? num.toFixed(4) : num.toFixed(2);
  };

  const estimateInvoice = (job) => {
    const hours = Number(job?.total_hours_worked || 0);
    const rate = Number(job?.service_rate || 0);
    const amount = hours * rate;
    return { hours, rate, amount };
  };

  const canGenerateInvoice = (job) => (
    !!canManageJobs &&
    !job.qb_invoice_id &&
    job.status === 'completed' &&
    Number(job.service_rate || 0) > 0 &&
    Number(job.total_hours_worked || 0) > 0
  );

  const handleGenerateInvoice = async () => {
    if (!invoiceJob) return;
    setGeneratingInvoice(true);
    try {
      const res = await axios.post(
        `${API}/qb/sync-job/${invoiceJob.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const est = estimateInvoice(invoiceJob);
      setInvoiceResult({
        invoice_id: res.data?.invoice_id || null,
        amount: est.amount,
        hours: est.hours,
        rate: est.rate,
        customer: invoiceJob.customer_name || customers.find(c => c.id === invoiceJob.customer_id)?.name || '',
        message: res.data?.message || 'Invoice created',
      });
      toast({ title: 'Invoice generated', description: `${formatCurrency(est.amount)} sent to QuickBooks Online.` });
      fetchJobs();
    } catch (err) {
      toast({
        title: 'Failed to generate invoice',
        description: err.response?.data?.detail || err.message || 'QuickBooks sync failed',
        variant: 'destructive',
      });
    } finally {
      setGeneratingInvoice(false);
    }
  };

  // Build a deep link to view the invoice in QuickBooks Online
  const qboInvoiceUrl = (invoiceId) => {
    if (!invoiceId) return null;
    const host = qbEnvironment === 'sandbox' ? 'app.sandbox.qbo.intuit.com' : 'app.qbo.intuit.com';
    return `https://${host}/app/invoice?txnId=${encodeURIComponent(invoiceId)}`;
  };

  // Bulk invoice generation — uses already-selected job IDs
  const handleBulkGenerateInvoices = async () => {
    const ids = Array.from(selectedJobIds || []);
    if (ids.length === 0) {
      toast({ title: 'No jobs selected', description: 'Pick one or more completed jobs first.', variant: 'destructive' });
      return;
    }
    setBulkInvoiceRunning(true);
    setBulkInvoiceResult(null);
    try {
      const res = await axios.post(
        `${API}/qb/sync-jobs-selected`,
        { job_ids: ids },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBulkInvoiceResult(res.data);
      toast({
        title: 'Bulk invoicing complete',
        description: res.data?.message || `${res.data?.synced || 0} sent`,
      });
      fetchJobs();
      // Clear selection so user doesn't accidentally re-run
      try { setSelectedJobIds(new Set()); } catch { /* not in scope */ }
    } catch (err) {
      toast({
        title: 'Bulk invoicing failed',
        description: err.response?.data?.detail || err.message || 'QuickBooks sync failed',
        variant: 'destructive',
      });
    } finally {
      setBulkInvoiceRunning(false);
    }
  };

  const fetchJobs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/jobs`, {
        headers: { Authorization: `Bearer ${token}` },
        params: isAdmin ? {} : { scope: jobScopeRef.current },
      });
      logger.debug('✅ Fetched jobs from API:', response.data.length, 'jobs');
      logger.debug('Jobs data:', response.data);
      setJobs(response.data);
    } catch (error) {
      console.error('❌ Failed to fetch jobs:', error);
    }
  }, [token, isAdmin]);

  const changeJobScope = (next, { persist = true } = {}) => {
    if (persist) saveJobScope(next);
    jobScopeRef.current = next;
    setJobScope(next);
    fetchJobs();
    if (activeTab === 'archived') fetchArchivedJobs();
  };

  // Company default arrived after first render and the worker never picked a
  // scope themselves → follow the company default.
  useEffect(() => {
    if (!defaultsLoaded || isAdmin || hasJobScopePref()) return;
    const want = companyDefaults.jobs_default_scope === 'team' ? 'team' : 'mine';
    if (want !== jobScopeRef.current) changeJobScope(want, { persist: false });
  }, [defaultsLoaded, companyDefaults.jobs_default_scope, isAdmin]);

  // Fetch per-job unread-notes counters for the small red pill next to each
  // job title. Called alongside fetchJobs and after the Notes tab is closed.
  const fetchUnreadNotes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/jobs/notes/unread-counts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUnreadNotes(data && typeof data === 'object' ? data : {});
    } catch {
      // Non-blocking — empty map is fine if the call ever fails
      setUnreadNotes({});
    }
  }, [token]);

  // Load the company's label catalog so each job card can render its chips.
  // Auto-seeds 6 defaults on first call server-side. Best-effort.
  const fetchLabels = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/labels`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCompanyLabels(Array.isArray(data) ? data : []);
    } catch {
      setCompanyLabels([]);
    }
  }, [token]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { scope: 'all' }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, [token]);

  const fetchTeams = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/teams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeams(response.data);
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    }
  }, [token]);

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
  }, [token]);

  const handleAddCustomerInline = async () => {
    if (!newCustomerName.trim()) return;
    try {
      const res = await axios.post(`${API}/customers`, {
        name: newCustomerName,
        email: newCustomerEmail || undefined,
        phone: newCustomerPhone || undefined
      }, { headers: { Authorization: `Bearer ${token}` } });
      setCustomers(prev => [...prev, res.data]);
      setNewJob(prev => ({
        ...prev,
        customer_id: res.data.id,
        customer_name: res.data.name,
        customer_email: res.data.email || ''
      }));
      setShowAddCustomerInline(false);
      setNewCustomerName('');
      setNewCustomerEmail('');
      setNewCustomerPhone('');
      toast({ title: 'Customer added', description: `${res.data.name} has been created.` });
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to add customer', variant: 'destructive' });
    }
  };


  // Build the photo URL. The backend endpoint returns a 307 redirect to a
  // signed S3 URL; the browser follows it transparently when used as an
  // <img> src, so we DON'T need to pre-fetch via axios. The previous
  // implementation tried to disable redirects with `maxRedirects: 0`, but
  // that flag is silently ignored by the browser XHR layer, and reading
  // `response.request.responseURL` afterwards proved unreliable in
  // production — when CORS stripped the post-redirect URL, photoUrl came
  // back empty and the UI showed "Failed to load". Direct URL works
  // everywhere and removes a network round-trip per thumbnail.
  const buildPhotoUrl = (jobId, photoFilename, { thumb = false } = {}) => {
    const base = `${API}/jobs/${jobId}/photos/${photoFilename}`;
    // Web sessions authenticate via the httpOnly cookie — only append the
    // token when we actually have one (native shell); never send "null".
    const params = [];
    if (token) params.push(`token=${encodeURIComponent(token)}`);
    if (thumb) params.push('thumb=1');
    return params.length ? `${base}?${params.join('&')}` : base;
  };

  // Photo Image Component
  // Renders an S3-backed photo as an <img>. On error, shows a single
  // "Tap to retry" pill. Retry simply remounts the <img> with a cache-busting
  // query param — no pre-flight fetch, no doubled network calls when S3 hiccups.
  const PhotoImage = ({ job, photo, className, onClick }) => {
    const [errored, setErrored] = React.useState(false);
    const [retryKey, setRetryKey] = React.useState(0);
    // Grid tiles load the small thumbnail (Jun 2026 perf); the lightbox
    // elsewhere still loads the full-resolution photo.
    const photoUrl = buildPhotoUrl(job.id, photo.filename, { thumb: true });

    if (errored) {
      return (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setErrored(false);
            setRetryKey((k) => k + 1);
          }}
          className={`${className} bg-[#0F1923] border-2 border-[#2D3748] flex flex-col items-center justify-center cursor-pointer hover:border-[#FFA000] transition-colors`}
          data-testid={`photo-retry-${photo.id}`}
          title="Tap to retry loading this photo"
        >
          <AlertCircle className="w-6 h-6 text-[#FFA000] mb-1" />
          <span className="text-[10px] text-[#FFA000] font-medium">Tap to retry</span>
        </button>
      );
    }

    return (
      <img
        key={retryKey}
        src={retryKey === 0 ? photoUrl : `${photoUrl}${photoUrl.includes('?') ? '&' : '?'}_r=${retryKey}`}
        alt={photo.original_filename}
        className={className}
        onClick={onClick}
        onError={() => setErrored(true)}
        loading="lazy"
        data-testid={`job-photo-${photo.id}`}
      />
    );
  };

  
  // Fetch employee statistics
  const fetchEmployeeStats = async (userId) => {
    if (employeeStats[userId]) return employeeStats[userId];
    
    setLoadingStats(prev => ({ ...prev, [userId]: true }));
    try {
      const response = await axios.get(`${API}/users/${userId}/job-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEmployeeStats(prev => ({ ...prev, [userId]: response.data }));
      return response.data;
    } catch (error) {
      console.error('Failed to fetch employee stats:', error);
      return null;
    } finally {
      setLoadingStats(prev => ({ ...prev, [userId]: false }));
    }
  };
  
  // Send job reminder
  const handleSendReminder = async (jobId) => {
    try {
      await axios.post(`${API}/jobs/${jobId}/send-reminder`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: 'Reminder Sent',
        description: 'Email reminder sent to assigned user',
      });
    } catch (error) {
      console.error('Failed to send reminder:', error);
      toast({
        title: 'Failed to Send Reminder',
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive'
      });
    }
  };
  
  // Build a quick name lookup so the search box can match assignee names
  // without re-iterating the users list on every keystroke.
  const userNameById = useMemo(() => {
    const m = {};
    for (const u of users) m[u.id] = u.name || '';
    return m;
  }, [users]);

  // Archive-aware search (Aug 2026): the main list only holds ACTIVE jobs, so
  // older archived/deleted work was unfindable. When a manager types a search,
  // also query the server across ALL company jobs and surface archived hits.
  useEffect(() => {
    const term = searchTerm.trim();
    if (!canManageJobs || term.length < 2) { setArchiveMatches([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/jobs/search-all`, {
          params: { q: term },
          headers: { Authorization: `Bearer ${token}` },
        });
        const loadedIds = new Set(jobs.map((j) => j.id));
        setArchiveMatches((data.matches || []).filter((m) => m.in_archive && !loadedIds.has(m.id)));
      } catch {
        setArchiveMatches([]);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [searchTerm, canManageJobs, token, jobs]);

  // Run the comprehensive search across the loaded jobs BEFORE we split by
  // status so each tab sees the filtered set. Case-insensitive substring
  // match across every field the user listed (Feb 2026).
  const searchedJobs = useMemo(() => {
    const term = (searchTerm || '').trim().toLowerCase();
    let list = jobs;
    if (term) {
      list = list.filter((j) => {
        const haystacks = [
          j.title,
          j.description,
          j.job_number,
          j.customer_name,
          j.job_address,
          j.street_address,
          j.city,
          j.status,
          j.paused_reason,
          userNameById[j.assigned_to],
          // Resolve label names for label-based searches ("emergency", "night")
          ...((j.label_ids || []).map((lid) => labelsById[lid]?.name).filter(Boolean)),
        ];
        return haystacks.some((h) => h && String(h).toLowerCase().includes(term));
      });
    }
    // One-tap label chip filter (Aug 2026)
    if (activeLabelFilter) {
      list = list.filter((j) => (j.label_ids || []).includes(activeLabelFilter));
    }
    return list;
  }, [jobs, searchTerm, userNameById, labelsById, activeLabelFilter]);

  // Filter jobs by status (memoized for performance).
  //
  // BUGFIX (Feb 2026): include `status === 'todo'` in the To-Do tab. The
  // backend /reassign endpoint historically wrote `todo` (still possible for
  // legacy docs and for pre-acceptance reassigns), but every status tab on
  // this page used to skip it — leaving such jobs invisible everywhere on
  // the /jobs screen even though admins saw them on /jobs/board (Kanban) and
  // /map. Rendering `todo` here keeps a single source of truth.
  const todoJobs = useMemo(() =>
    searchedJobs.filter(job =>
      job.status === 'todo' ||
      job.status === 'pending' ||
      (job.status === 'accepted' && !job.started_at)
    ), [searchedJobs]
  );
  
  const inProgressJobs = useMemo(() => 
    searchedJobs.filter(job => job.status === 'in_progress'), [searchedJobs]
  );
  
  const [staleOnly, setStaleOnly] = useState(false);
  const [staleDays, setStaleDays] = useState(30);
  useEffect(() => {
    if (!staleOnly || !token) return;
    axios.get(`${API}/job-housekeeping`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setStaleDays(r.data?.stale_job_days || 30)).catch(() => {});
  }, [staleOnly, token]);
  const pausedJobs = useMemo(() => {
    const cutoff = Date.now() - staleDays * 86400000;
    return searchedJobs.filter(job => job.status === 'paused' && (!staleOnly || (job.paused_at && new Date(job.paused_at).getTime() <= cutoff)));
  }, [searchedJobs, staleOnly, staleDays]);
  
  const completedJobs = useMemo(() => 
    // "Completed" now means done-but-not-yet-invoiced (Feb 2026 — admins
    // asked to split billed jobs into their own tab). Invoiced jobs move to
    // the new "Invoiced" bucket below.
    searchedJobs.filter(job => job.status === 'completed' && !job.qb_invoice_id), [searchedJobs]
  );

  const invoicedJobs = useMemo(() => 
    searchedJobs.filter(job => job.status === 'completed' && !!job.qb_invoice_id), [searchedJobs]
  );
  
  const archivedJobs = useMemo(() => 
    searchedJobs.filter(job => job.archived === true), [searchedJobs]
  );

  // ---- Pagination state (persisted in localStorage) ----
  const [pageSize, setPageSize] = useState(() => {
    try { return Number(localStorage.getItem('ht_jobs_page_size')) || 10; } catch { return 10; }
  });
  const [pages, setPages] = useState({ todo: 1, inprogress: 1, paused: 1, completed: 1, invoiced: 1 });

  useEffect(() => {
    try { localStorage.setItem('ht_jobs_page_size', String(pageSize)); } catch { /* ignore */ }
  }, [pageSize]);

  // Reset page when underlying list changes (so we don't end up beyond last page)
  useEffect(() => { setPages(p => ({ ...p, todo: 1 })); }, [todoJobs.length, pageSize]);
  useEffect(() => { setPages(p => ({ ...p, inprogress: 1 })); }, [inProgressJobs.length, pageSize]);
  useEffect(() => { setPages(p => ({ ...p, paused: 1 })); }, [pausedJobs.length, pageSize]);
  useEffect(() => { setPages(p => ({ ...p, completed: 1 })); }, [completedJobs.length, pageSize]);
  useEffect(() => { setPages(p => ({ ...p, invoiced: 1 })); }, [invoicedJobs.length, pageSize]);

  const paginate = (list, p) => {
    const start = (p - 1) * pageSize;
    return list.slice(start, start + pageSize);
  };
  const todoJobsPage = useMemo(() => paginate(todoJobs, pages.todo), [todoJobs, pages.todo, pageSize]);
  const inProgressJobsPage = useMemo(() => paginate(inProgressJobs, pages.inprogress), [inProgressJobs, pages.inprogress, pageSize]);
  const pausedJobsPage = useMemo(() => paginate(pausedJobs, pages.paused), [pausedJobs, pages.paused, pageSize]);
  const completedJobsPage = useMemo(() => paginate(completedJobs, pages.completed), [completedJobs, pages.completed, pageSize]);
  const invoicedJobsPage = useMemo(() => paginate(invoicedJobs, pages.invoiced), [invoicedJobs, pages.invoiced, pageSize]);

  // ---- Bulk selection (jobs) ----
  const [selectedJobIds, setSelectedJobIds] = useState(new Set());
  const [showBulkReassignDialog, setShowBulkReassignDialog] = useState(false);
  const [bulkAssigneeType, setBulkAssigneeType] = useState('user');
  const [bulkAssigneeId, setBulkAssigneeId] = useState('');
  const [bulkReassigning, setBulkReassigning] = useState(false);

  // Reset selection when underlying data, page, or filter changes
  useEffect(() => { setSelectedJobIds(new Set()); }, [pageSize, pages.todo, pages.inprogress, pages.paused]);

  const toggleSelectJob = (jobId) => {
    setSelectedJobIds((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId); else next.add(jobId);
      return next;
    });
  };

  const handleBulkReassign = async () => {
    if (selectedJobIds.size === 0 || !bulkAssigneeId) return;
    setBulkReassigning(true);
    try {
      const res = await axios.post(
        `${API}/jobs/bulk-reassign`,
        { job_ids: Array.from(selectedJobIds), assignee_type: bulkAssigneeType, assignee_id: bulkAssigneeId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const reassignedCount = res.data?.reassigned_count || 0;
      const skippedCount = (res.data?.skipped || []).length;
      toast({
        title: reassignedCount === 0 ? 'No jobs reassigned' : `Reassigned ${reassignedCount} job${reassignedCount !== 1 ? 's' : ''}`,
        description: skippedCount
          ? `${skippedCount} job${skippedCount !== 1 ? 's were' : ' was'} skipped (completed, archived, or out of scope).`
          : 'All selected jobs have been reassigned.',
      });
      setSelectedJobIds(new Set());
      setBulkAssigneeId('');
      setShowBulkReassignDialog(false);
      fetchJobs();
    } catch (error) {
      toast({
        title: 'Bulk reassign failed',
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setBulkReassigning(false);
    }
  };

  // Debug logging for job filtering
  useEffect(() => {
    logger.debug('📊 Job filtering results:');
    logger.debug('  Total jobs:', jobs.length);
    logger.debug('  To Do jobs:', todoJobs.length, todoJobs.map(j => ({ id: j.id, title: j.title, status: j.status })));
    logger.debug('  In Progress jobs:', inProgressJobs.length, inProgressJobs.map(j => ({ id: j.id, title: j.title, status: j.status })));
    logger.debug('  Completed jobs:', completedJobs.length, completedJobs.map(j => ({ id: j.id, title: j.title, status: j.status })));
  }, [jobs, todoJobs, inProgressJobs, completedJobs]);

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast({
        title: 'GPS Not Supported',
        description: 'Your browser does not support GPS location services',
        variant: 'destructive'
      });
      return;
    }

    setCapturingGPS(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setGpsCoordinates({
          ...gpsCoordinates,
          latitude: position.coords.latitude.toFixed(6),
          longitude: position.coords.longitude.toFixed(6)
        });
        setCapturingGPS(false);
        toast({
          title: 'Location Captured',
          description: `Lat: ${position.coords.latitude.toFixed(6)}, Lng: ${position.coords.longitude.toFixed(6)}`,
        });
      },
      (error) => {
        setCapturingGPS(false);
        toast({
          title: 'Location Error',
          description: 'Unable to capture your current location. Please enter coordinates manually.',
          variant: 'destructive'
        });
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Suggest labels this customer's past jobs usually carry (Aug 2026).
  useEffect(() => {
    const cid = newJob.customer_id;
    if (!showAddDialog || !cid) { setLabelSuggestions([]); return; }
    let cancelled = false;
    axios.get(`${API}/customers/${cid}/label-suggestions`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => { if (!cancelled) setLabelSuggestions(data.suggestions || []); })
      .catch(() => { if (!cancelled) setLabelSuggestions([]); });
    return () => { cancelled = true; };
  }, [showAddDialog, newJob.customer_id, token]);

  const handleAddJob = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      // Prepare job data based on location mode
      let jobData = { ...newJob };

      // Project link (Aug 2026): empty picker means "no project".
      if (!jobData.project_id) jobData.project_id = null;
      delete jobData.project_number;
      delete jobData.project_name;

      // Required-field validation: customer, scheduled dates, billing rate
      if (!jobData.customer_id) {
        toast({ title: 'Customer required', description: 'Please select or create a customer for this job.', variant: 'destructive' });
        setIsLoading(false);
        return;
      }
      if (!jobData.scheduled_start) {
        toast({ title: 'Scheduled start required', description: 'Please pick when this job should start.', variant: 'destructive' });
        setIsLoading(false);
        return;
      }
      if (!jobData.scheduled_completion) {
        toast({ title: 'Scheduled completion required', description: 'Please pick the target completion date/time.', variant: 'destructive' });
        setIsLoading(false);
        return;
      }
      if (jobData.service_rate === '' || jobData.service_rate === null || jobData.service_rate === undefined) {
        toast({ title: 'Billing rate required', description: 'Enter the hourly rate (e.g. 65).', variant: 'destructive' });
        setIsLoading(false);
        return;
      }
      const rateNum = Number(jobData.service_rate);
      if (Number.isNaN(rateNum) || rateNum < 0) {
        toast({ title: 'Invalid billing rate', description: 'Billing rate must be a non-negative number.', variant: 'destructive' });
        setIsLoading(false);
        return;
      }
      jobData.service_rate = rateNum;

      // Geofence radius: empty string means "use company default" — the
      // backend expects an int or null, and "" causes a 422 (Aug 2026 fix,
      // mirrors the sanitization handleEditJob already had).
      if (jobData.geofence_radius_meters === '' || jobData.geofence_radius_meters === 0) {
        jobData.geofence_radius_meters = null;
      } else if (jobData.geofence_radius_meters != null) {
        jobData.geofence_radius_meters = parseInt(jobData.geofence_radius_meters, 10);
        if (Number.isNaN(jobData.geofence_radius_meters)) jobData.geofence_radius_meters = null;
      }

      // Convert scheduled dates to ISO format
      jobData.scheduled_start = new Date(jobData.scheduled_start).toISOString();
      jobData.scheduled_completion = new Date(jobData.scheduled_completion).toISOString();
      
      // Handle assignment type
      if (assignmentType === 'team') {
        // Assign to team - clear assigned_to
        jobData.assigned_to = null;
        if (!jobData.assigned_team_id) {
          toast({
            title: 'Team Required',
            description: 'Please select a team to assign this job',
            variant: 'destructive'
          });
          setIsLoading(false);
          return;
        }
      } else {
        // Assign to one or more workers - clear assigned_team_id
        jobData.assigned_team_id = null;
        jobData.assigned_user_ids = (jobData.assigned_user_ids || []).filter(Boolean);
        if (jobData.assigned_user_ids.length === 0 && jobData.assigned_to) jobData.assigned_user_ids = [jobData.assigned_to];
        jobData.assigned_to = jobData.assigned_user_ids[0] || null;
        if (!jobData.assigned_to) {
          toast({
            title: 'Worker Required',
            description: 'Pick at least one worker to assign this job',
            variant: 'destructive'
          });
          setIsLoading(false);
          return;
        }
      }
      
      if (locationMode === 'gps') {
        // For GPS mode, send coordinates directly
        if (!gpsCoordinates.latitude || !gpsCoordinates.longitude) {
          toast({
            title: 'GPS Coordinates Required',
            description: 'Please enter or capture GPS coordinates',
            variant: 'destructive'
          });
          setIsLoading(false);
          return;
        }
        
        jobData = {
          ...jobData,
          location: {
            latitude: parseFloat(gpsCoordinates.latitude),
            longitude: parseFloat(gpsCoordinates.longitude),
            address: gpsCoordinates.description || 'Remote Location'
          },
          job_address: gpsCoordinates.description || `GPS: ${gpsCoordinates.latitude}, ${gpsCoordinates.longitude}`,
          // Clear address fields for GPS mode
          street_address: '',
          city: '',
          state_province: '',
          postal_code: '',
          country: ''
        };
      }
      
      const response = await axios.post(`${API}/jobs/create`, jobData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const createdJob = response.data;

      // Apply labels picked in the create form (reuses the existing
      // PUT /jobs/{id}/labels endpoint — sanitized + activity-logged).
      if (newJobLabelIds.length > 0 && createdJob?.id) {
        try {
          await axios.put(`${API}/jobs/${createdJob.id}/labels`,
            { label_ids: newJobLabelIds },
            { headers: { Authorization: `Bearer ${token}` } });
        } catch (labelErr) {
          console.error('Failed to apply labels to new job', labelErr);
          toast({ title: 'Warning', description: 'Job created but labels could not be applied. Add them from the job card.', variant: 'default' });
        }
      }
      
      // If PDF file is selected, upload it
      if (pdfFile) {
        try {
          setUploadingPdf(true);
          const formData = new FormData();
          formData.append('file', pdfFile);
          
          await axios.post(`${API}/jobs/${createdJob.id}/upload-pdf`, formData, {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          });
          
          toast({
            title: 'Success',
            description: assignmentType === 'team' ? 'Team job created with PDF attachment!' : 'Job created with PDF attachment!',
          });
        } catch (pdfError) {
          toast({
            title: 'Warning',
            description: 'Job created but PDF upload failed. You can attach it later.',
            variant: 'default'
          });
        } finally {
          setUploadingPdf(false);
        }
      } else {
        toast({
          title: 'Success',
          description: assignmentType === 'team' ? 'Team job created successfully!' : `Job created and assigned to ${(jobData.assigned_user_ids || []).length || 1} worker${(jobData.assigned_user_ids || []).length > 1 ? 's' : ''}!`,
        });
      }
      
      setShowAddDialog(false);
      setNewJob({ 
        title: '', 
        description: '', 
        assigned_to: '', 
        assigned_user_ids: [],
        assigned_team_id: '',
        customer_name: '', 
        customer_email: '',
        customer_id: '',
        scheduled_start: '',
        scheduled_completion: '',
        email_on_start: false,
        email_on_complete: false,
        street_address: '', 
        city: '', 
        state_province: '', 
        postal_code: '', 
        country: 'US', 
        photos_required: false,
        location_verification_required: false,
        geofence_radius_meters: '',
        is_extended: false,
        billable: true,
      });
      setPdfFile(null);
      setNewJobLabelIds([]);
      setLocationMode('address');
      setGpsCoordinates({ latitude: '', longitude: '', description: '' });
      setAssignmentType('user');
      fetchJobs();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || error.response?.data?.detail || 'Failed to create job',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleEditJob = async (e, openNext = false) => {
    if (e && e.preventDefault) e.preventDefault();
    setIsLoading(true);
    
    try {
      // Validate required fields
      if (!editJob.customer_id) {
        toast({ title: 'Customer required', variant: 'destructive' });
        setIsLoading(false);
        return;
      }
      if (!editJob.scheduled_start) {
        toast({ title: 'Scheduled start required', variant: 'destructive' });
        setIsLoading(false);
        return;
      }
      if (!editJob.scheduled_completion) {
        toast({ title: 'Scheduled completion required', variant: 'destructive' });
        setIsLoading(false);
        return;
      }
      if (editJob.service_rate === '' || editJob.service_rate === null || editJob.service_rate === undefined) {
        toast({ title: 'Billing rate required', variant: 'destructive' });
        setIsLoading(false);
        return;
      }
      const rateNum = Number(editJob.service_rate);
      if (Number.isNaN(rateNum) || rateNum < 0) {
        toast({ title: 'Invalid billing rate', variant: 'destructive' });
        setIsLoading(false);
        return;
      }

      // Sync customer_name from selected customer for legacy fields
      const selectedCustomer = customers.find(c => c.id === editJob.customer_id);

      const payload = {
        ...editJob,
        service_rate: rateNum,
        scheduled_start: new Date(editJob.scheduled_start).toISOString(),
        scheduled_completion: new Date(editJob.scheduled_completion).toISOString(),
        customer_name: selectedCustomer?.name || editJob.customer_name || '',
      };
      // Multi-worker reassignment (Sep 2026): only send the worker list when it
      // actually changed, so editing a title never touches a team job's ownership.
      {
        const orig = jobs.find((j) => j.id === editJob.id) || {};
        const origIds = (orig.assigned_user_ids && orig.assigned_user_ids.length) ? orig.assigned_user_ids : (!orig.assigned_team_id && orig.assigned_to ? [orig.assigned_to] : []);
        const nextIds = (editJob.assigned_user_ids || []).filter(Boolean);
        const same = nextIds.length === origIds.length && nextIds.every((id, i) => id === origIds[i]);
        if (same || nextIds.length === 0) delete payload.assigned_user_ids;
        if (same || nextIds.length === 0 || nextIds.length > 1) delete payload.assigned_to;
        delete payload.assigned_team_id;
      }
      // Coerce per-job geofence radius (Feb 2026). Empty / 0 clears the
      // override → server falls back to the company-wide 500 m default.
      if (payload.geofence_radius_meters === '' || payload.geofence_radius_meters === 0) {
        payload.geofence_radius_meters = null;
      } else if (payload.geofence_radius_meters != null) {
        payload.geofence_radius_meters = parseInt(payload.geofence_radius_meters, 10);
      }
      // Project payload normalisation (Feb 2026):
      //  - If user picked a real Project, `project_id` is a UUID — send it
      //    (backend resolves number/name from the canonical record).
      //  - If user typed a freeform number, `project_id` is '' and
      //    `project_number` is a raw string — send project_number, and
      //    convert project_id from '' to '' explicitly (which backend
      //    treats as "clear linked project").
      //  - We strip the display-only `project_name` since the backend
      //    doesn't honour it on edit (it caches the resolved name itself).
      delete payload.project_name;
      if (!payload.project_id) {
        payload.project_id = '';  // explicit clear so backend null-coalesces
      }

      await axios.put(`${API}/jobs/${editJob.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Find next job to edit BEFORE re-fetch (current `jobs` order)
      let nextJob = null;
      if (openNext) {
        const sameStatus = jobs.filter((j) => j.status === editJob.status && j.id !== editJob.id);
        const idx = sameStatus.findIndex((j) => (j.created_at || '') > (editJob.created_at || ''));
        nextJob = idx >= 0 ? sameStatus[idx] : (sameStatus[0] || null);
        if (!nextJob) {
          // Fall back to the next job overall (excluding the current one)
          const others = jobs.filter((j) => j.id !== editJob.id);
          nextJob = others[0] || null;
        }
      }
      
      toast({
        title: 'Success',
        description: openNext && nextJob
          ? `Job updated. Now editing: ${nextJob.job_number || nextJob.title}`
          : 'Job updated successfully!',
      });
      
      if (openNext && nextJob) {
        // Re-open the dialog with the next job's data (use openEditDialog for proper field formatting)
        openEditDialog(nextJob);
      } else {
        setShowEditDialog(false);
        setEditJob(null);
      }
      fetchJobs();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || error.response?.data?.detail || 'Failed to update job',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDeleteJob = async (jobId) => {
    // Feb 2026 — replace the old window.confirm with the unified reason
    // dialog so we capture WHY the job is being deleted (archive analytics)
    // and so archived jobs use the correct permanent-delete endpoint.
    const job = jobs.find(j => j.id === jobId) || archivedJobsList.find(j => j.id === jobId);
    if (!job) return;
    setDeleteJobTarget(job);
  };

  // Modal state for the "delete with reason" flow
  const [deleteJobTarget, setDeleteJobTarget] = useState(null);

  // Quick-note dialog (Feb 2026) — workers can capture a note attached to a
  // specific job no matter the job's status. Available on every job card.
  const [quickNoteJob, setQuickNoteJob] = useState(null);

  const performDeleteJob = async ({ reason }) => {
    const job = deleteJobTarget;
    if (!job) return;
    setIsLoading(true);
    try {
      if (job.archived) {
        // Permanently delete an already-archived job. Admin/super-user only.
        await axios.delete(`${API}/jobs/${job.id}/permanent`, {
          headers: { Authorization: `Bearer ${token}` },
          params: reason ? { reason } : {},
        });
        toast({ title: 'Permanently deleted', description: 'Job erased forever.' });
        fetchArchivedJobs();
      } else {
        // Soft-delete + auto-archive. Captures the chosen reason.
        await axios.delete(`${API}/jobs/${job.id}`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { deletion_reason: reason },
        });
        toast({ title: 'Archived', description: 'Job moved to the Archive page.' });
      }
      fetchJobs();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || error.response?.data?.detail || 'Failed to delete job',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const openEditDialog = (job) => {
    // Convert ISO datetimes to "yyyy-MM-ddTHH:mm" required by datetime-local input
    const toLocalInput = (iso) => {
      if (!iso) return '';
      try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return '';
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      } catch { return ''; }
    };
    setEditJob({
      id: job.id,
      title: job.title,
      description: job.description || '',
      assigned_to: job.assigned_to,
      assigned_user_ids: (job.assigned_user_ids && job.assigned_user_ids.length) ? job.assigned_user_ids : (!job.assigned_team_id && job.assigned_to ? [job.assigned_to] : []),
      assigned_team_id: job.assigned_team_id || '',
      customer_id: job.customer_id || '',
      customer_name: job.customer_name || '',
      street_address: job.street_address || '',
      city: job.city || '',
      state_province: job.state_province || '',
      postal_code: job.postal_code || '',
      country: job.country || 'US',
      photos_required: job.photos_required || false,
      // GPS verification flag — admin can toggle this on/off post-creation.
      // Defaults to false when missing on legacy jobs so the toggle reflects
      // the actual permissive state.
      location_verification_required: job.location_verification_required ?? false,
      // Per-job geofence radius override (Feb 2026). Empty string means
      // "use the company default" so the input stays blank by default.
      geofence_radius_meters: job.geofence_radius_meters ?? '',
      is_extended: job.is_extended || false,
      scheduled_start: toLocalInput(job.scheduled_start),
      scheduled_completion: toLocalInput(job.scheduled_completion),
      service_rate: job.service_rate ?? '',
      billable: job.billable !== false,
      // Project assignment (Feb 2026) — admins/supervisors can set, change,
      // or clear which project this job rolls up to. The project number
      // follows the job to invoice generation + the QuickBooks push.
      project_id: job.project_id || '',
      project_number: job.project_number || '',
      project_name: job.project_name || '',
    });
    setShowEditDialog(true);
  };

  const handleReassignJob = async (newAssigneeId, assigneeType = 'user') => {
    setIsLoading(true);
    try {
      // Backend's PUT /jobs/{id}/reassign doubles as the "assign" endpoint
      // when the job has no current assignee (Feb 2026 — estimate-converted
      // jobs land unassigned). Response carries `first_assignment: bool` so
      // we can show the right toast copy. assignee_type "team" reassigns to
      // a team or sub-team (Jun 2026).
      const { data } = await axios.put(
        `${API}/jobs/${selectedJob.id}/reassign?new_assignee_id=${newAssigneeId}&assignee_type=${assigneeType}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast({
        title: 'Success',
        description: assigneeType === 'team'
          ? `Job assigned to team ${data?.team_name} — ${data?.member_count} member${data?.member_count === 1 ? '' : 's'} notified.`
          : (data?.first_assignment
            ? 'Job assigned. The worker has been notified by email + SMS.'
            : 'Job reassigned successfully!'),
      });

      setShowReassignDialog(false);
      setSelectedJob(null);
      fetchJobs();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || error.response?.data?.message || 'Failed to reassign job',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptJob = async (jobId) => {
    setIsLoading(true);
    try {
      await axios.put(
        `${API}/jobs/${jobId}/accept`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({
        title: 'Success',
        description: 'Job accepted! You can now start working on it.',
      });
      
      fetchJobs();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || error.response?.data?.detail || 'Failed to accept job',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRejectJob = async () => {
    if (!rejectionReason.trim()) {
      toast({
        title: 'Error',
        description: 'Please provide a reason for rejecting this job',
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);
    try {
      await axios.put(
        `${API}/jobs/${selectedJob.id}/reject`,
        { rejection_reason: rejectionReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({
        title: 'Job Rejected',
        description: 'Your manager has been notified of the rejection.',
      });
      
      setShowRejectDialog(false);
      setSelectedJob(null);
      setRejectionReason('');
      fetchJobs();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || error.response?.data?.detail || 'Failed to reject job',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartJob = async (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    const isOnBehalf = !!(canManageJobs && job && job.assigned_to && job.assigned_to !== user.id && !canWorkJob(job));

    // Reuse the cached attestation on a paused → resume. Otherwise open
    // the safety dialog and let the user attest before we make the call.
    // Admins/supervisors starting "on behalf of" an offline worker also
    // attest — they're certifying the worker's fit-for-duty state, same
    // posture as signing a timesheet on the worker's behalf.
    if (!job?.safety_attestation) {
      setSafetyDialog({
        open: true,
        jobId,
        jobTitle: job?.title,
        isOnBehalf,
        isTeamJob: !!job?.assigned_team_id,
        teamName: job?.assigned_team_name || null,
      });
      return;
    }
    await _doStartJob(jobId, null);
  };

  // Lifted out of handleStartJob so the safety-dialog onAccept callback
  // and the resume-no-prompt path can share the actual /start invocation.
  const _doStartJob = async (jobId, attestation, outsideFence = null, qrToken = undefined) => {
    const job = jobs.find(j => j.id === jobId);
    const isOnBehalf = !!(canManageJobs && job && job.assigned_to && job.assigned_to !== user.id && !canWorkJob(job));
    // Site QR gate (Jun 2026): the job has a posted QR code → ask the
    // worker to scan it. Scanning marks the clock-in location-verified;
    // skipping falls back to the normal GPS flow.
    if (!isOnBehalf && job?.site_qr_required && qrToken === undefined && !outsideFence) {
      setQrScanDialog({ open: true, jobId, jobTitle: job?.title, attestation });
      return;
    }
    setIsLoading(true);
    const onBehalfQS = isOnBehalf ? `?on_behalf_of=${encodeURIComponent(job.assigned_to)}` : '';
    const workerName = isOnBehalf ? getUserName(job.assigned_to) : null;

    const callStart = async (lat, lon) => {
      try {
        const body = { latitude: lat, longitude: lon };
        if (attestation && attestation.accepted) {
          body.safety_attestation_accepted = true;
          body.safety_attestation_items = attestation.items;
          body.safety_attestation_version = attestation.version;
        }
        if (outsideFence && outsideFence.reason) {
          body.outside_geofence_reason = outsideFence.reason;
          if (outsideFence.notes) body.outside_geofence_notes = outsideFence.notes;
        }
        if (qrToken) body.site_qr_token = qrToken;
        await axios.put(
          `${API}/jobs/${jobId}/start${onBehalfQS}`,
          body,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast({
          title: 'Success',
          description: qrToken
            ? 'Job started — site verified by QR scan.'
            : outsideFence
            ? 'Job started — Travel Time is being logged until you arrive on site.'
            : (isOnBehalf
                ? `Job started on behalf of ${workerName}.`
                : 'Job started successfully!'),
        });
        fetchJobs();
      } catch (error) {
        // 403 outside geofence + no reason yet → open the OutsideGeofenceDialog
        // and re-invoke /start with the worker's reason on confirm. Detected
        // by the new "Travel Time" copy on the backend's 403 detail.
        const detail = error.response?.data?.detail || '';
        const isOutsideFence = error.response?.status === 403
          && /travel time/i.test(detail) && !outsideFence;
        if (isOutsideFence) {
          // Extract the distance/radius from the detail copy if possible.
          const distMatch = detail.match(/(\d+)\s*m\s*from/i);
          const radMatch = detail.match(/max\s*(\d+)\s*m/i);
          setGeofenceDialog({
            open: true,
            jobId,
            jobTitle: job?.title,
            distanceMeters: distMatch ? parseInt(distMatch[1], 10) : null,
            radiusMeters: radMatch ? parseInt(radMatch[1], 10) : null,
            attestation,
            lat, lon,
          });
        } else {
          toast({
            title: 'Error',
            description: detail || error.response?.data?.message || 'Failed to start job',
            variant: 'destructive'
          });
        }
      } finally {
        setIsLoading(false);
      }
    };

    // On-behalf flows (admin/supervisor) skip GPS — worker is offline / phoning in.
    if (isOnBehalf) {
      await callStart(0, 0);
      return;
    }

    // Normal flow: capture GPS first.
    if (!navigator.geolocation) {
      if (qrToken) { await callStart(null, null); return; }
      toast({
        title: 'Error',
        description: 'Your browser does not support location services',
        variant: 'destructive'
      });
      setIsLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => callStart(position.coords.latitude, position.coords.longitude),
      () => {
        if (qrToken) { callStart(null, null); return; }
        toast({
          title: 'Location Required',
          description: 'Please enable location services to start this job. You must be at the job location.',
          variant: 'destructive'
        });
        setIsLoading(false);
      }
    );
  };

  const handleCompleteJob = async (jobId, overridePhotos = false) => {
    const job = jobs.find(j => j.id === jobId);
    
    // Check if photos are required and no photos exist yet
    if (job?.photos_required && (!job.photos || job.photos.length === 0) && !overridePhotos) {
      // Admins may override the photo requirement (e.g. crew forgot to
      // upload but the job is done and needs to go to billing).
      if (isAdmin) {
        setPhotoOverrideModal({ open: true, job });
        return;
      }
      setSelectedJob(job);
      setShowPhotoDialog(true);
      toast({
        title: 'Photos Required',
        description: 'Please upload at least one photo before completing this job',
        variant: 'default'
      });
      return;
    }

    const isOnBehalf = !!(canManageJobs && job && job.assigned_to && job.assigned_to !== user.id && !canWorkJob(job));
    const params = new URLSearchParams();
    if (isOnBehalf) params.set('on_behalf_of', job.assigned_to);
    if (overridePhotos) params.set('override_photos', 'true');
    const qs = params.toString() ? `?${params.toString()}` : '';
    const workerName = isOnBehalf ? getUserName(job.assigned_to) : null;

    setIsLoading(true);
    try {
      await axios.put(`${API}/jobs/${jobId}/complete${qs}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: 'Success',
        description: isOnBehalf
          ? `Job completed on behalf of ${workerName}.`
          : 'Job completed successfully!',
      });
      
      fetchJobs();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || error.response?.data?.message || 'Failed to complete job',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Pause modal state (required reason + optional notes)
  const [pauseModal, setPauseModal] = useState({ open: false, jobId: null, reason: '', notes: '' });
  // Admin-only: override the "photos required" gate when completing a job
  const [photoOverrideModal, setPhotoOverrideModal] = useState({ open: false, job: null });

  const openPauseModal = (jobId) => setPauseModal({ open: true, jobId, reason: '', notes: '' });
  const closePauseModal = () => setPauseModal({ open: false, jobId: null, reason: '', notes: '' });

  const handlePauseJob = async () => {
    const { jobId, reason, notes } = pauseModal;
    if (!jobId) return;
    if (!reason) {
      toast({ title: 'Reason required', description: 'Please select a pause reason.', variant: 'destructive' });
      return;
    }
    // Admin/Supervisor pausing on behalf of an offline worker — same pattern
    // as start-/complete-/resume-on-behalf. The job's assignee stays the same;
    // the audit trail records both the operator and the original assignee.
    const job = jobs.find(j => j.id === jobId);
    const isOnBehalf = !!(canManageJobs && job && job.assigned_to && job.assigned_to !== user.id && !canWorkJob(job));
    const onBehalfQS = isOnBehalf ? `?on_behalf_of=${encodeURIComponent(job.assigned_to)}` : '';
    const workerName = isOnBehalf ? getUserName(job.assigned_to) : null;

    setIsLoading(true);
    try {
      await axios.put(`${API}/jobs/${jobId}/pause${onBehalfQS}`, { reason, notes: notes || null }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: isOnBehalf ? `Paused on behalf of ${workerName}` : 'Job Paused',
        description: isOnBehalf
          ? `${workerName} has been notified. Add any progress notes from the Job Detail dialog.`
          : 'Supervisor has been notified. You can resume it anytime.',
      });

      closePauseModal();
      fetchJobs();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to pause job',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResumeJob = async (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    const isOnBehalf = !!(canManageJobs && job && job.assigned_to && job.assigned_to !== user.id && !canWorkJob(job));
    const onBehalfQS = isOnBehalf ? `?on_behalf_of=${encodeURIComponent(job.assigned_to)}` : '';
    const workerName = isOnBehalf ? getUserName(job.assigned_to) : null;
    setIsLoading(true);
    try {
      await axios.put(`${API}/jobs/${jobId}/resume${onBehalfQS}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: 'Job Resumed',
        description: isOnBehalf ? `Job resumed on behalf of ${workerName}.` : 'Job resumed successfully. Continue working!',
      });
      
      fetchJobs();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to resume job',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };


  // Archive operations
  const [archiveJobTarget, setArchiveJobTarget] = useState(null);
  const handleArchiveJob = (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    if (job) setArchiveJobTarget(job);
  };

  const performArchiveJob = async (reason, notifyCustomer = false) => {
    const job = archiveJobTarget;
    if (!job) return;
    setIsLoading(true);
    try {
      const { data } = await axios.post(`${API}/jobs/${job.id}/archive`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        params: { reason, notify_customer: notifyCustomer ? 'true' : 'false' },
      });
      const bits = [];
      if (data?.closed_sessions) bits.push(`Stopped the clock for ${data.closed_sessions} crew member(s).`);
      if (notifyCustomer) bits.push(data?.customer_notified ? 'Cancellation confirmation emailed to the customer.' : 'Customer email could not be sent (no address or mail error).');
      bits.push('Restore it any time from the Archive tab.');
      toast({ title: reason === 'cancelled_by_customer' ? 'Job Cancelled & Archived' : 'Job Archived', description: bits.join(' ') });
      setArchiveJobTarget(null);
      fetchJobs();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to archive job',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnarchiveJob = async (jobId) => {
    setIsLoading(true);
    try {
      await axios.post(`${API}/jobs/${jobId}/unarchive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: 'Job Restored',
        description: 'Job restored from archive successfully',
      });
      
      fetchArchivedJobs();
      fetchJobs();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to restore job',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadInvoicePdf = async (job) => {
    try {
      const resp = await axios.get(`${API}/jobs/${job.id}/invoice-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const disp = resp.headers?.['content-disposition'] || '';
      const m = disp.match(/filename="?([^";]+)"?/i);
      const filename = m ? m[1] : `invoice-${job.id.slice(0, 8)}.pdf`;
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
      toast({ title: 'Invoice downloaded', description: filename });
      fetchJobs();
    } catch (err) {
      toast({
        title: 'Could not download invoice',
        description: err.response?.data?.detail || err.message || 'Please retry.',
        variant: 'destructive',
      });
    }
  };

  const handleSendInvoiceEmail = async () => {
    if (!emailInvoiceJob) return;
    const hasPhotos = (emailInvoiceJob.photos || []).length > 0;
    setEmailInvoiceSending(true);
    try {
      const r = await axios.post(
        `${API}/jobs/${emailInvoiceJob.id}/invoice-pdf/email`,
        {
          to: emailInvoiceTo || undefined,
          message: emailInvoiceMessage || undefined,
          include_photo_report: hasPhotos && includePhotoReport,
          include_photos_zip: hasPhotos && includePhotosZip,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast({
        title: 'Invoice emailed',
        description: `${r.data?.invoice_number || 'Invoice'} sent to ${r.data?.to}`,
      });
      setEmailInvoiceJob(null);
      setEmailInvoiceTo('');
      setEmailInvoiceMessage('');
      setIncludePhotoReport(true);
      setIncludePhotosZip(false);
      fetchJobs();
    } catch (err) {
      toast({
        title: 'Could not email invoice',
        description: err.response?.data?.detail || err.message || 'Please retry.',
        variant: 'destructive',
      });
    } finally {
      setEmailInvoiceSending(false);
    }
  };


  const handleAutoArchive = async () => {
    if (!window.confirm('Archive all completed jobs older than 90 days?\n\nThey will be moved to the Archive tab and can be restored anytime.')) return;
    
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/jobs/auto-archive?days=90`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: 'Auto-Archive Complete',
        description: `${response.data.archived_count} jobs archived successfully`,
      });
      
      fetchJobs();
      fetchArchivedJobs();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to auto-archive jobs',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchArchivedJobs = async () => {
    setLoadingArchive(true);
    try {
      const params = {
        page: archivePagination.page,
        limit: archivePagination.limit
      };
      
      if (archiveSearch) params.search = archiveSearch;
      if (archiveFilters.start_date) params.start_date = archiveFilters.start_date;
      if (archiveFilters.end_date) params.end_date = archiveFilters.end_date;
      if (archiveFilters.worker_id) params.worker_id = archiveFilters.worker_id;
      if (archiveFilters.location) params.location = archiveFilters.location;
      if (!isAdmin) params.scope = jobScopeRef.current;
      
      const response = await axios.get(`${API}/jobs/archived`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setArchivedJobsList(response.data.jobs);
      setArchivePagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to fetch archived jobs:', error);
      toast({
        title: 'Error',
        description: 'Failed to load archived jobs',
        variant: 'destructive'
      });
    } finally {
      setLoadingArchive(false);
    }
  };

  const handleArchiveSearch = () => {
    setArchivePagination(prev => ({ ...prev, page: 1 }));
    fetchArchivedJobs();
  };

  const handleClearArchiveFilters = () => {
    setArchiveSearch('');
    setArchiveFilters({
      start_date: '',
      end_date: '',
      worker_id: '',
      location: ''
    });
    setArchivePagination(prev => ({ ...prev, page: 1 }));
  };

  // Fetch archived jobs when Archive tab is active
  useEffect(() => {
    if (activeTab === 'archived' && token) {
      fetchArchivedJobs();
    }
  }, [activeTab, token, archivePagination.page]);


  // Photo-upload handler. Defensive on input so a call-site signature
  // mismatch (e.g. someone passing `(jobId, fileList)` instead of
  // `(event, job)`) fails LOUDLY with a toast instead of silently
  // returning. This is the bug that hit Lara on the Complete-Job dialog
  // in Jun 2026 — `event.target.files` was `undefined` and the function
  // exited without any error, leaving the user staring at a dialog that
  // "did nothing" when they tapped Upload (Jun 2026 hardening).
  const handlePhotoUpload = async (event, job) => {
    const files = event?.target?.files;
    const jobId = job?.id;

    // Hard contract checks — surface a toast for every failure mode so the
    // user is never left guessing whether their click registered.
    if (!event || typeof event !== 'object' || !('target' in event)) {
      console.error('handlePhotoUpload called with bad event arg:', event);
      toast({
        title: 'Upload could not start',
        description: 'Something went wrong wiring up the upload button. Refresh the page and try again — we are tracking this error.',
        variant: 'destructive',
      });
      return;
    }
    if (!jobId) {
      toast({
        title: 'Upload could not start',
        description: 'This job is no longer open. Please refresh and try again.',
        variant: 'destructive',
      });
      return;
    }
    if (!files || files.length === 0) {
      // Genuine "user cancelled file picker" path — no toast (would be spammy).
      return;
    }

    const currentPhotoCount = job?.photos?.length || 0;
    const maxPhotos = 10;
    const MAX_SIZE_BYTES = 25 * 1024 * 1024; // 25MB — must match backend
    // Image-extension whitelist (case-insensitive). Trust the file extension
    // since mobile Safari often leaves `file.type` empty for HEIC photos
    // pulled from the camera roll, causing a MIME whitelist to silently
    // reject perfectly valid images. Backend re-validates.
    const ALLOWED_EXT = ['.jpg', '.jpeg', '.png', '.heic', '.heif', '.webp'];

    if (currentPhotoCount >= maxPhotos) {
      toast({
        title: 'Maximum photos reached',
        description: `You can only upload up to ${maxPhotos} photos per job`,
        variant: 'destructive',
      });
      return;
    }

    const remainingSlots = maxPhotos - currentPhotoCount;
    const filesToUpload = Array.from(files).slice(0, remainingSlots);
    setUploadingPhoto(true);

    // Upload all files in parallel using allSettled so one failure doesn't
    // halt the rest. Each file reports its own per-file toast on failure;
    // we still show a single success toast at the end summarising successes.
    const results = await Promise.allSettled(filesToUpload.map(async (rawFile) => {
      // Extension check on the SOURCE file (forgiving: ignore empty file.type)
      const dot = rawFile.name.lastIndexOf('.');
      const ext = dot >= 0 ? rawFile.name.slice(dot).toLowerCase() : '';
      if (!ALLOWED_EXT.includes(ext)) {
        throw new Error(`${rawFile.name}: only JPG, PNG, HEIC, HEIF, or WEBP images are supported`);
      }
      // Size check (mirror backend) — applied to the SOURCE; compression
      // typically shrinks the result well below this anyway.
      if (rawFile.size > MAX_SIZE_BYTES) {
        const mb = (rawFile.size / (1024 * 1024)).toFixed(1);
        throw new Error(`${rawFile.name}: ${mb} MB exceeds 25 MB limit`);
      }

      // Compress + normalize to JPEG before sending. Drops a 12 MB iPhone HEIC
      // to ~500–800 KB with no visible loss, cuts cellular data ~95%, and
      // ensures everything downstream (browsers, invoice PDFs, QuickBooks
      // attachments) can render the image without HEIC support. Falls back
      // to the original file when decode is unsupported (rare).
      const file = await compressImage(rawFile);

      const reportPct = (pct) => setPhotoUploadProgress((m) => ({ ...m, [rawFile.name]: pct }));
      try {
        // Fast path (Jun 2026): direct-to-S3 presigned upload — the file goes
        // browser → S3 without transiting the app server. Falls back to the
        // legacy proxied upload if presign/bucket-CORS isn't available.
        try {
          const data = await uploadJobPhotoDirect({
            apiBase: API, jobId, token, file,
            originalName: rawFile.name, onProgress: reportPct,
          });
          return data;
        } catch (directErr) {
          const st = directErr?.response?.status;
          if (st === 400 || st === 403) throw directErr; // real validation error — surface it
          console.warn('Direct S3 upload unavailable, falling back to proxied upload:', directErr?.message);
        }
        const formData = new FormData();
        formData.append('file', file);
        const response = await axios.post(`${API}/jobs/${jobId}/upload-photo`, formData, {
          headers: { Authorization: `Bearer ${token}` },
          // 5-minute timeout per photo. Cellular uploads of 20+ MB HEIC photos
          // can take 30-60s — the previous 60s timeout cut them off mid-upload.
          timeout: 300000,
          maxContentLength: 30 * 1024 * 1024,
          maxBodyLength: 30 * 1024 * 1024,
          onUploadProgress: (e) => { if (e.total) reportPct(Math.round((e.loaded / e.total) * 100)); },
        });
        return response.data;
      } catch (err) {
        if (err.code === 'ECONNABORTED' || err.message === 'Network Error' || !err.response) {
          // Offline (Jun 2026): stash the compressed photo in IndexedDB —
          // it uploads automatically when connectivity returns.
          try {
            await enqueuePhoto({ jobId, file });
            return { queued: true, name: rawFile.name };
          } catch {
            throw new Error(`${rawFile.name}: network error — check your internet connection and retry`);
          }
        }
        // Surface the backend's actual error message ("File size exceeds…",
        // "File type not allowed…") so users know what to fix.
        const backendMsg = err.response?.data?.detail;
        throw new Error(backendMsg ? `${rawFile.name}: ${backendMsg}` : `${rawFile.name}: ${err.message || 'upload failed'}`);
      }
    }));

    setUploadingPhoto(false);
    setPhotoUploadProgress({});
    event.target.value = '';

    const fulfilled = results.filter((r) => r.status === 'fulfilled');
    const queuedCount = fulfilled.filter((r) => r.value?.queued).length;
    const successes = fulfilled.length - queuedCount;
    const failures = results.filter((r) => r.status === 'rejected');
    if (successes > 0) {
      toast({
        title: 'Photos uploaded',
        description: `${successes} of ${filesToUpload.length} photo${filesToUpload.length === 1 ? '' : 's'} uploaded successfully`,
      });
      fetchJobs();
    }
    if (queuedCount > 0) {
      toast({
        title: 'Saved offline',
        description: `${queuedCount} photo${queuedCount === 1 ? '' : 's'} saved on this device — they'll upload automatically when you're back online`,
      });
    }
    failures.forEach((r) => {
      toast({
        title: 'Upload failed',
        description: r.reason?.message || 'Upload failed',
        variant: 'destructive',
      });
    });
  };

  const handleDeletePhoto = async (jobId, photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;
    
    try {
      await axios.delete(`${API}/jobs/${jobId}/photos/${photoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: 'Success',
        description: 'Photo deleted successfully',
      });
      
      fetchJobs();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to delete photo',
        variant: 'destructive'
      });
    }
  };

  const openPhotoGallery = (job) => {
    setSelectedJob(job);
    setShowPhotoGallery(true);
  };

  // Download all photos on a job as a ZIP archive (Feb 2026).
  // Uses an auth-token query param style consistent with the existing
  // photo view route so the browser can stream the ZIP without us
  // having to fetch into memory first.
  const downloadPhotosZip = async (job) => {
    if (!job?.photos?.length) {
      toast({ title: 'No photos to download', variant: 'destructive' });
      return;
    }
    try {
      const res = await axios.get(`${API}/jobs/${job.id}/photos/zip`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `job-${job.job_number || job.id}-photos.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: 'Photos downloaded', description: `${job.photos.length} photo${job.photos.length === 1 ? '' : 's'} bundled.` });
    } catch (e) {
      toast({
        title: 'Download failed',
        description: e.response?.data?.detail || e.message || 'Could not bundle photos',
        variant: 'destructive',
      });
    }
  };

  const downloadPhotoReport = async (job) => {
    if (!job?.photos?.length) {
      toast({ title: 'No photos to report', variant: 'destructive' });
      return;
    }
    try {
      const res = await axios.get(`${API}/jobs/${job.id}/photos/report-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `job-${job.job_number || job.id}-photo-report.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: 'Photo report ready', description: 'PDF downloaded.' });
    } catch (e) {
      toast({
        title: 'Report failed',
        description: e.response?.data?.detail || e.message || 'Could not generate report',
        variant: 'destructive',
      });
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      todo: { label: 'To Do', className: 'bg-[#0F1923] text-white border-[#2D3748]' },
      pending: { label: 'Pending', className: 'bg-[#0F1923] text-white border-[#2D3748]' },
      accepted: { label: 'Accepted', className: 'bg-[#0F1923] text-white border-[#2D3748]' },
      in_progress: { label: 'In Progress', className: 'bg-[#1A2433] text-blue-800 border-[#2D3748]' },
      paused: { label: 'Paused', className: 'bg-[#1A2433] text-yellow-800 border-[#FFA000]' },
      completed: { label: 'Completed', className: 'bg-[#1A2433] text-green-800 border-[#00C896]' },
      rejected: { label: 'Rejected', className: 'bg-[#1A2433] text-red-800 border-red-300' }
    };
    
    const config = statusConfig[status] || statusConfig.todo;
    return (
      <Badge className={`${config.className} border font-semibold px-3 py-1`}>
        {config.label}
      </Badge>
    );
  };

  const isJobScheduleOverdue = (job) => {
    if (job.status === 'completed') return false;
    if (!job.scheduled_completion) return false;
    return new Date(job.scheduled_completion) < new Date();
  };

  const getUserName = (userId) => {
    if (userId && userId === user?.id) return user?.name || 'Me';
    const foundUser = users.find(u => u.id === userId);
    return foundUser ? foundUser.name : 'Unknown';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1A2433] via-white to-[#1A2433] pb-4">
      {/* Header */}
      <div className="w-full px-3 sm:px-4 md:px-6 lg:px-8 py-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <p className="text-slate-400">
              {canManageJobs ? 'Create, assign, and manage jobs' : 'View and track your assignments'}
            </p>
          </div>
          {canManageJobs && (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setBulkQrOpen(true)}
                className="bg-[#1A2433] text-white border-[#2D3748] hover:bg-[#2D3748] hover:text-white"
                data-testid="open-bulk-qr-btn"
              >
                <QrCode className="w-4 h-4 mr-2" />
                QR Posters
              </Button>
              <Button
                variant="outline"
                onClick={() => setManageLabelsOpen(true)}
                className="bg-[#1A2433] text-white border-[#2D3748] hover:bg-[#2D3748] hover:text-white"
                data-testid="open-manage-labels-btn"
              >
                <Tag className="w-4 h-4 mr-2" />
                Labels
              </Button>
              <AddJobDialog
                open={showAddDialog}
                onOpenChange={setShowAddDialog}
                newJob={newJob}
                setNewJob={setNewJob}
                assignmentType={assignmentType}
                setAssignmentType={setAssignmentType}
                users={users}
                teams={teams}
                customers={customers}
                showAddCustomerInline={showAddCustomerInline}
                setShowAddCustomerInline={setShowAddCustomerInline}
                newCustomerName={newCustomerName}
                setNewCustomerName={setNewCustomerName}
                newCustomerEmail={newCustomerEmail}
                setNewCustomerEmail={setNewCustomerEmail}
                newCustomerPhone={newCustomerPhone}
                setNewCustomerPhone={setNewCustomerPhone}
                onAddCustomerInline={handleAddCustomerInline}
                locationMode={locationMode}
                setLocationMode={setLocationMode}
                gpsCoordinates={gpsCoordinates}
                setGpsCoordinates={setGpsCoordinates}
                capturingGPS={capturingGPS}
                onCaptureCurrentLocation={getCurrentLocation}
                pdfFile={pdfFile}
                setPdfFile={setPdfFile}
                labelIds={newJobLabelIds}
                setLabelIds={setNewJobLabelIds}
                labelSuggestions={labelSuggestions}
                token={token}
                onSubmit={handleAddJob}
                isLoading={isLoading}
              />
            </div>
            )}
          </div>
      
      {/* Edit Job Dialog - Admin Only (extracted Feb 2026) */}
      <EditJobDialog
        open={isAdmin && !!editJob && showEditDialog}
        onOpenChange={setShowEditDialog}
        editJob={editJob}
        setEditJob={setEditJob}
        users={users}
        customers={customers}
        editProjOpen={editProjOpen}
        setEditProjOpen={setEditProjOpen}
        editProjResults={editProjResults}
        setEditProjResults={setEditProjResults}
        onSubmit={handleEditJob}
        isLoading={isLoading}
        jobsCount={jobs.length}
        token={token}
      />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-4">
        {/* Empty-category notice from dashboard deep-links */}
        {emptyNotice && (
          <div
            className="mb-4 flex items-center justify-between gap-3 px-4 py-3 rounded-xl"
            style={{ background: 'rgba(255,160,0,0.12)', border: '1px solid rgba(255,160,0,0.4)' }}
            data-testid="jobs-empty-notice"
          >
            <p className="text-sm font-medium" style={{ color: '#B45309' }}>
              There are currently 0 jobs in the “{emptyNotice}” category.
            </p>
            <button
              onClick={() => setEmptyNotice(null)}
              className="text-xs px-2.5 py-1 rounded-lg flex-shrink-0"
              style={{ color: '#B45309', border: '1px solid rgba(255,160,0,0.4)' }}
              data-testid="jobs-empty-notice-dismiss"
            >
              Dismiss
            </button>
          </div>
        )}
        {/* Bulk action bar (visible when at least one job is selected) */}
        {canManageJobs && selectedJobIds.size > 0 && (
          <div
            className="flex items-center justify-between gap-3 px-4 py-3 mb-4 rounded-lg"
            style={{ background: 'rgba(30,136,229,0.10)', border: '1px solid rgba(30,136,229,0.4)' }}
            data-testid="jobs-bulk-action-bar"
          >
            <p className="text-sm text-white font-medium">
              <span data-testid="jobs-bulk-selected-count">{selectedJobIds.size}</span> job{selectedJobIds.size !== 1 ? 's' : ''} selected
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSelectedJobIds(new Set())}
                data-testid="jobs-bulk-clear-btn"
              >
                Clear
              </Button>
              <Button
                size="sm"
                onClick={() => setShowBulkReassignDialog(true)}
                className="bg-[#1E88E5] hover:bg-blue-700 text-white"
                data-testid="jobs-bulk-reassign-btn"
              >
                <Users className="w-4 h-4 mr-1.5" />
                Reassign selected
              </Button>
              {activeTab === 'completed' && qbConnected && canBillOut && (
                <Button
                  size="sm"
                  onClick={() => { setBulkInvoiceOpen(true); setBulkInvoiceResult(null); }}
                  className="text-white"
                  style={{ background: '#2CA01C' }}
                  data-testid="jobs-bulk-invoice-btn"
                >
                  <Send className="w-4 h-4 mr-1.5" />
                  Generate {selectedJobIds.size} invoice{selectedJobIds.size !== 1 ? 's' : ''}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Worker/supervisor scope: my jobs vs my team's jobs (Sep 2026) */}
        {!isAdmin && (
          <div className="mb-3">
            <JobScopeToggle scope={jobScope} onChange={changeJobScope} />
          </div>
        )}

        {/* Comprehensive job search (Feb 2026) — client-side filter across
            all active tabs. Searches assignee name, job number, title (WO),
            description, customer, address, status, paused reason, and labels. */}
        <div className="mb-4">
          <div
            className="relative flex items-center"
            style={{ background: '#0F1923', border: '1px solid #2D3748', borderRadius: 10 }}
          >
            <Search className="w-4 h-4 ml-3 flex-shrink-0" style={{ color: '#64748B' }} />
            <Input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={
                canManageJobs
                  ? 'Search jobs by assignee, WO #, job #, customer, location, description, label…'
                  : 'Search your jobs by WO #, customer, location, description…'
              }
              className="flex-1 bg-transparent border-0 text-white text-sm focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-slate-500 h-10"
              data-testid="jobs-page-search-input"
              aria-label="Search jobs"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="p-2 rounded text-slate-500 hover:text-white"
                data-testid="jobs-page-search-clear"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            {canManageJobs && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setBulkPhotoOpen(true)}
                className="mr-1.5 text-slate-400 hover:text-white flex-shrink-0"
                title="Download all job photos in a date range as a ZIP"
                data-testid="jobs-bulk-photo-download-btn"
              >
                <Camera className="w-4 h-4 mr-1.5" />
                <span className="hidden sm:inline">Photos ZIP</span>
              </Button>
            )}
          </div>
          {/* Label filter chips (Aug 2026) — one tap to see all jobs with a label */}
          {companyLabels.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid="jobs-label-filter-row">
              <Tag className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#64748B' }} />
              {companyLabels.map((l) => {
                const active = activeLabelFilter === l.id;
                return (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => setActiveLabelFilter(active ? null : l.id)}
                    className="px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide transition-all active:scale-95"
                    style={{
                      background: active ? (l.color || '#1E88E5') : `${l.color || '#1E88E5'}1A`,
                      color: active ? '#fff' : (l.color || '#94A3B8'),
                      border: `1px solid ${active ? (l.color || '#1E88E5') : `${l.color || '#1E88E5'}55`}`,
                    }}
                    data-testid={`label-filter-chip-${l.id}`}
                  >
                    {l.name}
                  </button>
                );
              })}
              {activeLabelFilter && (
                <button
                  type="button"
                  onClick={() => setActiveLabelFilter(null)}
                  className="px-2 py-1 rounded-full text-[11px] transition-colors hover:text-white"
                  style={{ color: '#94A3B8', border: '1px solid #2D3748' }}
                  data-testid="label-filter-clear"
                >
                  Clear ✕
                </button>
              )}
            </div>
          )}
          {(searchTerm.trim() || activeLabelFilter) && (
            <p className="text-xs mt-1.5 ml-1" style={{ color: '#94A3B8' }} data-testid="jobs-page-search-summary">
              Matched <span className="font-semibold text-white">{searchedJobs.length}</span> of{' '}
              <span className="font-semibold text-white">{jobs.length}</span> jobs across all tabs
            </p>
          )}
          {searchTerm.trim() && archiveMatches.length > 0 && (
            <div className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3" data-testid="jobs-archive-matches">
              <p className="text-xs font-semibold text-amber-300 mb-1.5">
                {archiveMatches.length} older job{archiveMatches.length > 1 ? 's' : ''} matching &quot;{searchTerm.trim()}&quot; found in the archive:
              </p>
              <div className="space-y-1">
                {archiveMatches.slice(0, 5).map((m) => (
                  <p key={m.id} className="text-xs text-slate-300 truncate" data-testid={`archive-match-${m.job_number}`}>
                    <span className="font-mono text-amber-200">{m.job_number}</span> · {m.title}
                    {m.customer_name ? ` — ${m.customer_name}` : ''}
                  </p>
                ))}
                {archiveMatches.length > 5 && (
                  <p className="text-xs text-slate-400">…and {archiveMatches.length - 5} more</p>
                )}
              </div>
              <Button
                size="sm"
                variant="outline"
                className="mt-2 h-7 text-xs border-amber-500/50 text-amber-200 hover:bg-amber-500/20"
                onClick={() => navigate(`/archive?q=${encodeURIComponent(searchTerm.trim())}`)}
                data-testid="jobs-archive-matches-view-btn"
              >
                View Archive
              </Button>
            </div>
          )}
        </div>

        {/* Enhanced Tabs System */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 sm:grid-cols-6 gap-1 mb-6 h-auto">
            <TabsTrigger 
              value="todo" 
              className="data-[state=active]:bg-[#1E88E5] data-[state=active]:text-white flex flex-col items-center py-3 px-1"
              data-testid="tab-todo"
            >
              <ClipboardList className="w-5 h-5 mb-1" />
              <span className="font-semibold text-xs">{t('jobs.tab.todo', 'To Do')}</span>
              <span className="text-xs opacity-90">({todoJobs.length})</span>
            </TabsTrigger>
            <TabsTrigger 
              value="inprogress" 
              className="data-[state=active]:bg-orange-600 data-[state=active]:text-white flex flex-col items-center py-3 px-1"
              data-testid="tab-inprogress"
            >
              <Activity className="w-5 h-5 mb-1" />
              <span className="font-semibold text-xs">{t('jobs.tab.inProgress', 'In Progress')}</span>
              <span className="text-xs opacity-90">({inProgressJobs.length})</span>
            </TabsTrigger>
            <TabsTrigger 
              value="paused" 
              className="data-[state=active]:bg-purple-600 data-[state=active]:text-white flex flex-col items-center py-3 px-1"
              data-testid="tab-paused"
            >
              <Clock className="w-5 h-5 mb-1" />
              <span className="font-semibold text-xs">{t('jobs.tab.paused', 'Paused')}</span>
              <span className="text-xs opacity-90">({pausedJobs.length})</span>
            </TabsTrigger>
            <TabsTrigger 
              value="completed" 
              className="data-[state=active]:bg-[#00C896] data-[state=active]:text-white flex flex-col items-center py-3 px-1"
              data-testid="tab-completed"
            >
              <CheckCircle2 className="w-5 h-5 mb-1" />
              <span className="font-semibold text-xs">{t('jobs.tab.completed', 'Completed')}</span>
              <span className="text-xs opacity-90">({completedJobs.length})</span>
            </TabsTrigger>
            <TabsTrigger 
              value="invoiced" 
              className="data-[state=active]:bg-emerald-700 data-[state=active]:text-white flex flex-col items-center py-3 px-1"
              data-testid="tab-invoiced"
            >
              <ReceiptIcon className="w-5 h-5 mb-1" />
              <span className="font-semibold text-xs">{t('jobs.tab.invoiced', 'Invoiced')}</span>
              <span className="text-xs opacity-90">({invoicedJobs.length})</span>
            </TabsTrigger>
            <TabsTrigger 
              value="archived" 
              className="data-[state=active]:bg-gray-600 data-[state=active]:text-white flex flex-col items-center py-3 px-1"
              data-testid="tab-archived"
            >
              <FileText className="w-5 h-5 mb-1" />
              <span className="font-semibold text-xs">{t('jobs.tab.archive', 'Archive')}</span>
              <span className="text-xs opacity-90">({archivePagination.total_count || 0})</span>
            </TabsTrigger>
          </TabsList>

          {/* TO DO TAB - Enhanced */}
          <TabsContent value="todo" className="mt-6">
            {todoJobs.length === 0 ? (
              <Card className="border-2 border-dashed border-[#2D3748]">
                <CardContent className="p-12 text-center">
                  <Briefcase className="w-16 h-16 mx-auto text-blue-400 mb-4" />
                  <p className="text-slate-500 text-lg font-medium">No pending jobs</p>
                  <p className="text-gray-400 text-sm mt-2">All jobs are either in progress or completed</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {todoJobsPage.map((job) => {
                  const isOverdue = isJobOverdue(job.created_at);
                  const timeSince = getTimeSinceAssignment(job.created_at);
                  return (
                    <JobRowCard
                      key={job.id}
                      job={job}
                      variant="todo"
                      labelsById={labelsById}
                      getUserName={getUserName}
                      getStatusBadge={getStatusBadge}
                      isOverdue={isOverdue}
                      isJobScheduleOverdue={isJobScheduleOverdue(job)}
                      timeSince={timeSince}
                      canManageJobs={canManageJobs}
                      isSelected={selectedJobIds.has(job.id)}
                      onToggleSelect={toggleSelectJob}
                      onOpenDetail={setDetailJob}
                      unreadNotes={unreadNotes[job.id] || 0}
                      users={users}
                      onCrewChanged={fetchJobs}
                      testIdPrefix="todo"
                    >
                      {/* Admin Actions */}
                      {canManageJobs && (
                        <div className="flex flex-wrap gap-2 pt-3 border-t">
                          <Button
                            size="sm"
                            onClick={() => handleStartJob(job.id)}
                            disabled={isLoading}
                            className="bg-orange-600 hover:bg-orange-700 text-white"
                            data-testid={`admin-start-job-${job.id}`}
                            title={!canWorkJob(job) && job.assigned_to ? `Start on behalf of ${getUserName(job.assigned_to)} (skips GPS check)` : 'Start your job'}
                          >
                            <Play className="w-4 h-4 mr-1" />
                            {!canWorkJob(job) && job.assigned_to ? 'Start (on behalf)' : 'Start Job'}
                          </Button>
                          <input
                            type="file"
                            id={`photo-upload-todo-admin-${job.id}`}
                            accept="image/*"
                            multiple
                            onChange={(e) => handlePhotoUpload(e, job)}
                            className="hidden"
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openAdminPhotos(job)}
                            data-testid={`admin-add-photos-${job.id}`}
                            disabled={uploadingPhoto || (job.photos && job.photos.length >= 10)}
                            className="border-[#1E88E5]/60 text-sky-300 hover:bg-[#0F1923] disabled:opacity-40"
                          >
                            <Camera className="w-4 h-4 mr-1" />
                            Add Photos ({job.photos ? job.photos.length : 0}/10)
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setQuickNoteJob(job)}
                            className="border-amber-500 text-amber-300 hover:bg-amber-500/10"
                            data-testid={`add-note-job-${job.id}`}
                            title="Add a note to this job"
                          >
                            <StickyNote className="w-4 h-4 mr-1" />
                            Add a Note
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedJob(job);
                              setShowReassignDialog(true);
                            }}
                            className={job.assigned_to ? "border-[#2D3748] text-blue-700 hover:bg-[#0F1923]" : "border-emerald-500 text-emerald-700 hover:bg-emerald-50"}
                            data-testid={`reassign-job-${job.id}`}
                          >
                            <UserCog className="w-4 h-4 mr-1" />
                            {job.assigned_to ? 'Reassign' : 'Assign worker'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSendReminder(job.id)}
                            className="border-[#FFA000] text-orange-700 hover:bg-[#0F1923]"
                          >
                            <Mail className="w-4 h-4 mr-1" />
                            Send Reminder
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditDialog(job)}
                            className="border-[#2D3748] text-slate-300 hover:bg-[#1A2433]"
                            data-testid={`edit-job-todo-${job.id}`}
                          >
                            <Edit2 className="w-4 h-4 mr-1" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleArchiveJob(job.id)}
                            className="border-[#FFA000] text-[#FFA000] hover:bg-[#FFA000]/10"
                            data-testid={`archive-job-todo-${job.id}`}
                            title="Archive for later (seasonal hold, cancelled, on hold) — restore any time"
                          >
                            <FileText className="w-4 h-4 mr-1" />
                            Archive
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setWorkbenchJob(job)}
                            className="border-[#1E88E5] text-[#1E88E5] hover:bg-[#1E88E5]/10"
                            data-testid={`open-workbench-${job.id}`}
                            title="Job Workbench — crew, time and costs"
                          >
                            <Wrench className="w-4 h-4 mr-1" />
                            Workbench
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDeleteJob(job.id)}
                            className="ml-auto"
                            data-testid={`delete-job-${job.id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete
                          </Button>
                        </div>
                      )}
                      {/* User Actions */}
                      {!canManageJobs && canWorkJob(job) && (
                        <div className="flex flex-wrap gap-2 pt-3 border-t">
                          <Button
                            size="sm"
                            onClick={() => handleStartJob(job.id)}
                            disabled={isLoading}
                            className="bg-orange-600 hover:bg-orange-700 text-white"
                          >
                            <Play className="w-4 h-4 mr-1" />
                            Start Job
                          </Button>
                          <input
                            type="file"
                            id={`photo-upload-todo-user-${job.id}`}
                            accept="image/*"
                            multiple
                            onChange={(e) => handlePhotoUpload(e, job)}
                            className="hidden"
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => document.getElementById(`photo-upload-todo-user-${job.id}`).click()}
                            disabled={uploadingPhoto || (job.photos && job.photos.length >= 10)}
                            className="border-[#2D3748] text-blue-700 hover:bg-[#0F1923]"
                          >
                            <Camera className="w-4 h-4 mr-1" />
                            Add Photos ({job.photos ? job.photos.length : 0}/10)
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setQuickNoteJob(job)}
                            className="border-amber-500 text-amber-300 hover:bg-amber-500/10"
                            data-testid={`add-note-job-${job.id}`}
                            title="Add a note to this job"
                          >
                            <StickyNote className="w-4 h-4 mr-1" />
                            Add a Note
                          </Button>
                        </div>
                      )}
                    </JobRowCard>
                  );
                })}
              </div>
            )}
            <Pagination
              total={todoJobs.length}
              page={pages.todo}
              pageSize={pageSize}
              onPageChange={(p) => setPages(s => ({ ...s, todo: p }))}
              onPageSizeChange={setPageSize}
              testId="todo-pagination"
              itemLabel="job"
            />
          </TabsContent>

          {/* IN PROGRESS TAB - Enhanced */}
          <TabsContent value="inprogress" className="mt-6">
            <InProgressJobsTab
              inProgressJobs={inProgressJobs}
              inProgressJobsPage={inProgressJobsPage}
              openEditDialog={openEditDialog}
              handleArchiveJob={handleArchiveJob}
              handleDeleteJob={handleDeleteJob}
              canDeleteJobs={isAdmin}
              user={user}
              canManageJobs={canManageJobs}
              canWorkJob={canWorkJob}
              getUserName={getUserName}
              getJobDuration={getJobDuration}
              selectedJobIds={selectedJobIds}
              toggleSelectJob={toggleSelectJob}
              unreadNotes={unreadNotes}
              labelsById={labelsById}
              setDetailJob={setDetailJob}
              setWorkbenchJob={setWorkbenchJob}
              users={users}
              onCrewChanged={fetchJobs}
              setQuickNoteJob={setQuickNoteJob}
              setSelectedJob={setSelectedJob}
              setShowReassignDialog={setShowReassignDialog}
              openPhotoGallery={openPhotoGallery}
              handlePhotoUpload={handlePhotoUpload}
              openAdminPhotos={canManageJobs ? openAdminPhotos : undefined}
              uploadingPhoto={uploadingPhoto}
              openPauseModal={openPauseModal}
              handleCompleteJob={handleCompleteJob}
              isLoading={isLoading}
              pages={pages}
              setPages={setPages}
              pageSize={pageSize}
              setPageSize={setPageSize}
            />
          </TabsContent>

          {/* PAUSED TAB - Multi-Day Jobs */}
          <TabsContent value="paused" className="mt-6">
            {staleOnly && (
              <div className="flex items-center gap-3 mb-3 px-3 py-2 rounded-lg text-xs" style={{ background: 'rgba(255,160,0,0.12)', border: '1px solid rgba(255,160,0,0.4)', color: '#FFA000' }} data-testid="paused-stale-filter-banner">
                Showing only jobs paused {staleDays}+ days ({pausedJobs.length}). Archive them for next season, cancel or resume.
                <button onClick={() => { setStaleOnly(false); navigate('/jobs?tab=paused', { replace: true }); }} className="ml-auto underline" data-testid="paused-stale-filter-clear">Show all paused</button>
              </div>
            )}
            <PausedJobsTab
              pausedJobs={pausedJobs}
              pausedJobsPage={pausedJobsPage}
              openEditDialog={openEditDialog}
              handleArchiveJob={handleArchiveJob}
              handleDeleteJob={handleDeleteJob}
              canDeleteJobs={isAdmin}
              user={user}
              canManageJobs={canManageJobs}
              canWorkJob={canWorkJob}
              getUserName={getUserName}
              selectedJobIds={selectedJobIds}
              toggleSelectJob={toggleSelectJob}
              unreadNotes={unreadNotes}
              labelsById={labelsById}
              setDetailJob={setDetailJob}
              setWorkbenchJob={setWorkbenchJob}
              users={users}
              onCrewChanged={fetchJobs}
              setQuickNoteJob={setQuickNoteJob}
              setSelectedJob={setSelectedJob}
              setShowReassignDialog={setShowReassignDialog}
              openPhotoGallery={openPhotoGallery}
              handlePhotoUpload={handlePhotoUpload}
              openAdminPhotos={canManageJobs ? openAdminPhotos : undefined}
              uploadingPhoto={uploadingPhoto}
              handleResumeJob={handleResumeJob}
              handleCompleteJob={handleCompleteJob}
              isLoading={isLoading}
              pages={pages}
              setPages={setPages}
              pageSize={pageSize}
              setPageSize={setPageSize}
            />
          </TabsContent>

          {/* COMPLETED TAB - Enhanced */}
          <TabsContent value="completed" className="mt-6">
            {completedJobs.length === 0 ? (
              <Card className="border-2 border-dashed border-[#00C896]">
                <CardContent className="p-12 text-center">
                  <CheckCircle2 className="w-16 h-16 mx-auto text-green-400 mb-4" />
                  <p className="text-slate-500 text-lg font-medium">No completed jobs yet</p>
                  <p className="text-gray-400 text-sm mt-2">Completed jobs will appear here</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {completedJobsPage.map((job) => {
                  const duration = getJobDuration(job.started_at, job.completed_at);
                  const workerName = (job.is_team_job && job.team_name)
                    ? `Team · ${job.team_parent_name ? `${job.team_parent_name} › ${job.team_name}` : job.team_name}`
                    : ((job.crew_names && job.crew_names[job.assigned_to]) || getUserName(job.assigned_to));
                  
                  return (
                    <Card key={job.id} className="relative shadow-md border-l-4 border-[#00C896] bg-[#1A2433]">
                      {canManageJobs && !job.qb_invoice_id && (
                        <input
                          type="checkbox"
                          aria-label={`Select job ${job.job_number || job.title}`}
                          checked={selectedJobIds.has(job.id)}
                          onChange={() => toggleSelectJob(job.id)}
                          className="absolute top-3 right-3 w-4 h-4 cursor-pointer accent-blue-500 z-10"
                          data-testid={`select-job-${job.id}`}
                          title="Select for bulk Generate Invoice"
                        />
                      )}
                      <CardContent className="p-5">
                        <div className="space-y-3">
                          {/* Header with checkmark */}
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <h3 className="text-lg font-bold text-white flex items-baseline gap-2 flex-wrap">{job.job_number && <span className="text-xs font-mono text-[#1E88E5] bg-[#1E88E5]/10 px-2 py-0.5 rounded" data-testid={`job-number-${job.id}`}>{job.job_number}</span>}<button type="button" onClick={(e) => { e.stopPropagation(); setDetailJob(job); }} className="text-left hover:text-blue-400 hover:underline transition-colors cursor-pointer" data-testid={`open-job-detail-${job.id}`} title="Open job details (notes, files, activity)">{job.title}</button>{unreadNotes[job.id] > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-500 text-white animate-pulse" title={`${unreadNotes[job.id]} unread note${unreadNotes[job.id] === 1 ? '' : 's'}`} data-testid={`unread-notes-badge-${job.id}`}>{unreadNotes[job.id]} new</span>}{canManageJobs && <button type="button" onClick={(e) => { e.stopPropagation(); setWorkbenchJob(job); }} className="text-slate-500 hover:text-[#1E88E5] transition-colors" data-testid={`open-workbench-${job.id}`} title="Job Workbench — crew, time and costs"><Wrench className="w-4 h-4" /></button>}</h3>
                                <Badge className="bg-[#0F1923]0 text-white">✅ Completed</Badge>
                                {job.photos_overridden && (
                                  <span className="ht-pill text-xs" style={{ background: 'rgba(255,160,0,0.12)', color: '#F0A020', border: '1px solid rgba(255,160,0,0.4)' }} title="An admin completed this job without the required photos" data-testid={`photos-overridden-badge-${job.id}`}>
                                    <AlertCircle className="w-3 h-3 inline mr-1" />No photos
                                  </span>
                                )}
                              </div>
                              {(job.label_ids || []).length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`job-labels-row-${job.id}`}>
                                  {(job.label_ids || []).map((lid) => labelsById[lid]).filter(Boolean).map((l) => (
                                    <LabelChip key={l.id} label={l} size="sm" />
                                  ))}
                                </div>
                              )}
                              {job.description && (
                                <p className="text-sm text-slate-400 mb-2">{job.description}</p>
                              )}
                            </div>
                          </div>

                          {/* Photo Thumbnails */}
                          {job.photos && job.photos.length > 0 && (
                            <div className="bg-[#1A2433] p-3 rounded-lg">
                              <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                                <span className="text-sm font-semibold text-slate-300">📸 Job Photos</span>
                                <div className="flex items-center gap-1.5">
                                  {canManageJobs && (
                                    <>
                                      <button
                                        onClick={() => downloadPhotosZip(job)}
                                        className="text-[11px] px-2 py-1 rounded font-medium hover:bg-white/5 transition-colors"
                                        style={{ color: '#1E88E5', border: '1px solid #2D3748' }}
                                        title="Download all photos as ZIP"
                                        data-testid={`download-zip-${job.id}`}
                                      >
                                        <Download className="w-3 h-3 inline -mt-0.5" /> ZIP
                                      </button>
                                      <button
                                        onClick={() => downloadPhotoReport(job)}
                                        className="text-[11px] px-2 py-1 rounded font-medium hover:bg-white/5 transition-colors"
                                        style={{ color: '#00C896', border: '1px solid #2D3748' }}
                                        title="Generate printable PDF photo report"
                                        data-testid={`download-photo-report-${job.id}`}
                                      >
                                        <FileText className="w-3 h-3 inline -mt-0.5" /> PDF
                                      </button>
                                    </>
                                  )}
                                  <button
                                    onClick={() => openPhotoGallery(job)}
                                    className="text-xs text-[#1E88E5] hover:underline font-medium"
                                  >
                                    View All ({job.photos.length})
                                  </button>
                                </div>
                              </div>
                              <div className="grid grid-cols-3 gap-2">
                                {job.photos.slice(0, 3).map((photo, idx) => (
                                  <PhotoImage
                                    key={photo.id}
                                    job={job}
                                    photo={photo}
                                    className="w-full h-20 object-cover rounded-lg border-2 border-[#2D3748] cursor-zoom-in hover:border-green-400"
                                    onClick={() => {
                                      const images = job.photos.map((p) => ({
                                        src: buildPhotoUrl(job.id, p.filename),
                                        name: p.original_filename,
                                        id: p.id,
                                      }));
                                      setPhotoLightbox({ images, index: idx });
                                    }}
                                  />
                                ))}
                                {job.photos.length > 3 && (
                                  <div className="w-full h-20 bg-[#0F1923] rounded-lg border-2 border-dashed border-[#2D3748] flex items-center justify-center">
                                    <div className="text-center">
                                      <Image className="w-6 h-6 mx-auto text-gray-400 mb-1" />
                                      <span className="text-xs text-slate-500 font-medium">+{job.photos.length - 3} more</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Job Details Grid */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm bg-[#1A2433] p-3 rounded-lg">
                            <div className="flex items-center space-x-2 text-slate-300">
                              <User className="w-4 h-4 text-[#00C896]" />
                              <span className="font-medium">Completed by:</span>
                              <span>{workerName}</span>
                            </div>
                            <div className="flex items-center space-x-2 text-slate-300">
                              <Clock className="w-4 h-4 text-[#00C896]" />
                              <span className="font-medium">Duration:</span>
                              <span className="font-semibold text-green-700">{duration}</span>
                            </div>
                            <div className="flex items-center space-x-2 text-slate-300">
                              <CheckCircle2 className="w-4 h-4 text-[#00C896]" />
                              <span className="font-medium">Completed:</span>
                              <span>{formatDateTimeWithTimezone(job.completed_at, user?.timezone || 'America/Toronto')}</span>
                            </div>
                            {job.customer_name && (
                              <div className="flex items-center space-x-2 text-slate-300">
                                <User className="w-4 h-4 text-[#00C896]" />
                                <span className="font-medium">Customer:</span>
                                <span>{job.customer_name}</span>
                              </div>
                            )}
                            {job.job_address && (
                              <div className="flex items-center space-x-2 text-slate-300 col-span-2">
                                <MapPin className="w-4 h-4 text-[#00C896]" />
                                <span className="font-medium">Location:</span>
                                <span className="truncate">{job.job_address}</span>
                              </div>
                            )}
                            {(job.started_on_behalf || job.completed_on_behalf) && (
                              <div className="flex items-center space-x-2 text-amber-400 text-xs col-span-2" data-testid={`on-behalf-audit-${job.id}`}>
                                <UserCog className="w-3.5 h-3.5" />
                                <span className="font-medium">
                                  {job.completed_on_behalf
                                    ? `Completed on behalf by ${job.completed_by_name || 'admin'}`
                                    : `Started on behalf by ${job.started_by_name || 'admin'}`}
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Completion Notes */}
                          {job.completion_notes && (
                            <div className="bg-[#0F1923] p-3 rounded-lg border-l-4 border-yellow-400">
                              <div className="flex items-start space-x-2">
                                <FileText className="w-4 h-4 text-yellow-700 mt-0.5" />
                                <div className="flex-1">
                                  <span className="text-sm font-semibold text-yellow-900">Completion Notes:</span>
                                  <p className="text-sm text-yellow-800 mt-1">{job.completion_notes}</p>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Actions */}
                          <div className="flex flex-wrap gap-2 pt-3 border-t">
                            <input
                              type="file"
                              id={`photo-upload-completed-${job.id}`}
                              accept="image/*"
                              multiple
                              onChange={(e) => handlePhotoUpload(e, job)}
                              className="hidden"
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => (canManageJobs ? openAdminPhotos(job) : document.getElementById(`photo-upload-completed-${job.id}`).click())}
                              data-testid={`admin-add-photos-${job.id}`}
                              disabled={uploadingPhoto || (job.photos && job.photos.length >= 10)}
                              className="border-[#00C896]/60 text-emerald-300 hover:bg-[#0F1923] disabled:opacity-40"
                            >
                              <Camera className="w-4 h-4 mr-1" />
                              Add Photos ({job.photos ? job.photos.length : 0}/10)
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setQuickNoteJob(job)}
                              className="border-amber-500 text-amber-300 hover:bg-amber-500/10"
                              data-testid={`add-note-job-${job.id}`}
                              title="Add a note to this job"
                            >
                              <StickyNote className="w-4 h-4 mr-1" />
                              Add a Note
                            </Button>
                            {job.photos && job.photos.length > 0 && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openPhotoGallery(job)}
                                  className="border-[#00C896] text-green-700 hover:bg-[#0F1923]"
                                >
                                  <Camera className="w-4 h-4 mr-1" />
                                  View Photos
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={async () => {
                                    setSelectedJob(job);
                                    setEmailRecipient(job.customer_email || '');
                                    setShowEmailPhotosDialog(true);
                                  }}
                                  className="border-[#2D3748] text-blue-700 hover:bg-[#0F1923]"
                                >
                                  <Mail className="w-4 h-4 mr-1" />
                                  Email Photos
                                </Button>
                              </>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedJob(job);
                                setEmailRecipient(job.customer_email || '');
                                setIncludePhotosInReport(job.photos && job.photos.length > 0);
                                setShowEmailReportDialog(true);
                              }}
                              className="border-purple-300 text-purple-700 hover:bg-[#0F1923]"
                            >
                              <Send className="w-4 h-4 mr-1" />
                              Email Report
                            </Button>
                            {canManageJobs && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={async () => {
                                  const stats = await fetchEmployeeStats(job.assigned_to);
                                  if (stats) {
                                    toast({
                                      title: `${workerName}'s Statistics`,
                                      description: `Completed: ${stats.completed_jobs}/${stats.total_jobs} jobs (${stats.completion_rate}%) | Avg Time: ${stats.average_completion_time}`,
                                      duration: 5000
                                    });
                                  }
                                }}
                                disabled={loadingStats[job.assigned_to]}
                                className="border-[#2D3748] text-slate-300 hover:bg-[#1A2433]"
                              >
                                {loadingStats[job.assigned_to] ? (
                                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                ) : (
                                  <User className="w-4 h-4 mr-1" />
                                )}
                                View Stats
                              </Button>
                            )}
                            {canManageJobs && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleArchiveJob(job.id)}
                                disabled={isLoading}
                                className="border-[#2D3748] text-slate-300 hover:bg-[#1A2433]"
                                data-testid={`archive-job-completed-${job.id}`}
                              >
                                <FileText className="w-4 h-4 mr-1" />
                                Archive
                              </Button>
                            )}
                            {/* HinesTime native PDF — QB-independent. Always
                                available on completed jobs as long as the
                                customer can be billed. */}
                            {canManageJobs && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDownloadInvoicePdf(job)}
                                className="border-[#1E88E5] text-[#1E88E5] hover:bg-[#1E88E5] hover:text-white"
                                data-testid={`download-invoice-pdf-${job.id}`}
                                title="Download a branded PDF invoice for this job"
                              >
                                <FileText className="w-4 h-4 mr-1" />
                                Download PDF
                              </Button>
                            )}
                            {canManageJobs && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setEmailInvoiceJob(job)}
                                className="border-[#1E88E5] text-[#1E88E5] hover:bg-[#1E88E5] hover:text-white"
                                data-testid={`email-invoice-pdf-${job.id}`}
                                title="Email a PDF invoice to the customer"
                              >
                                <Mail className="w-4 h-4 mr-1" />
                                Email Invoice
                              </Button>
                            )}
                            {canManageJobs && !job.qb_invoice_id && qbConnected && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setTestSyncJob(job)}
                                className="border-[#1E88E5] text-[#1E88E5] hover:bg-[#1E88E5] hover:text-white"
                                data-testid={`test-sync-${job.id}`}
                                title="Run a QuickBooks preflight check without creating the invoice"
                              >
                                <ClipboardList className="w-4 h-4 mr-1" />
                                Test Sync
                              </Button>
                            )}
                            {canBillOut && !job.qb_invoice_id && (() => {
                              const hasRate = !!job.service_rate && Number(job.service_rate) > 0;
                              const hasHours = !!job.total_hours_worked && Number(job.total_hours_worked) > 0;
                              const ready = hasRate && hasHours;
                              const label = ready
                                ? 'Open invoice editor'
                                : !hasRate
                                  ? 'Set rate & materials, then invoice'
                                  : 'Open invoice editor';
                              return (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => { setInvoiceJob(job); setInvoiceResult(null); }}
                                  className="border-[#2CA01C] text-[#2CA01C] hover:bg-[#2CA01C] hover:text-white"
                                  data-testid={`generate-invoice-${job.id}`}
                                  title={label}
                                >
                                  <Send className="w-4 h-4 mr-1" />
                                  Generate Invoice
                                </Button>
                              );
                            })()}
                            <InvoiceNumberBadge job={job} />
                            {job.qb_invoice_id && (
                              <span
                                className="ht-pill ht-pill-green text-xs"
                                data-testid={`qb-invoiced-badge-${job.id}`}
                                title={`QBO Invoice #${job.qb_invoice_id}`}
                              >
                                Invoiced · #{String(job.qb_invoice_id).slice(0, 8)}
                              </span>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
                <Pagination
                  total={completedJobs.length}
                  page={pages.completed}
                  pageSize={pageSize}
                  onPageChange={(p) => setPages(s => ({ ...s, completed: p }))}
                  onPageSizeChange={setPageSize}
                  testId="completed-pagination"
                  itemLabel="job"
                />
              </div>
            )}
          </TabsContent>

          {/* INVOICED TAB — completed jobs that have a QBO invoice attached (Feb 2026) */}
          <TabsContent value="invoiced" className="mt-6">
            {invoicedJobs.length === 0 ? (
              <Card className="border-2 border-dashed border-emerald-700">
                <CardContent className="p-12 text-center">
                  <ReceiptIcon className="w-16 h-16 mx-auto text-emerald-400 mb-4" />
                  <p className="text-slate-300 text-lg font-medium">No invoiced jobs yet</p>
                  <p className="text-slate-500 text-sm mt-2">
                    Jobs move here automatically once you generate a QuickBooks invoice from the Completed tab.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3" data-testid="invoiced-list">
                {invoicedJobsPage.map((job) => {
                  const workerName = (job.is_team_job && job.team_name)
                    ? `Team · ${job.team_parent_name ? `${job.team_parent_name} › ${job.team_name}` : job.team_name}`
                    : ((job.crew_names && job.crew_names[job.assigned_to]) || getUserName(job.assigned_to));
                  const duration = getJobDuration(job.started_at, job.completed_at);
                  return (
                    <Card
                      key={job.id}
                      className="shadow-md border-l-4 border-emerald-600 bg-[#1A2433] cursor-pointer hover:bg-[#1F2937] transition-colors"
                      onClick={() => setDetailJob(job)}
                      data-testid={`invoiced-card-${job.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              {job.job_number && (
                                <span className="text-xs font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded">
                                  {job.job_number}
                                </span>
                              )}
                              <h4 className="font-semibold text-white text-base hover:text-blue-400 hover:underline transition-colors" data-testid={`open-job-detail-${job.id}`} title="Open job details (notes, photos, activity)">{job.title}</h4>
                            </div>
                            {job.customer_name && (
                              <p className="text-xs text-slate-400">
                                <span className="text-slate-500">Customer:</span> {job.customer_name}
                              </p>
                            )}
                          </div>
                          <InvoiceNumberBadge job={job} />
                          <span
                            className="ht-pill ht-pill-green text-xs"
                            data-testid={`invoiced-badge-${job.id}`}
                            title={`QBO Invoice #${job.qb_invoice_id}`}
                          >
                            <ReceiptIcon className="w-3 h-3 inline mr-1" />
                            Invoiced · #{String(job.qb_invoice_id || '').slice(0, 8)}
                          </span>
                          {job.photos_overridden && (
                            <span className="ht-pill text-xs ml-2" style={{ background: 'rgba(255,160,0,0.12)', color: '#F0A020', border: '1px solid rgba(255,160,0,0.4)' }} title="An admin completed this job without the required photos" data-testid={`photos-overridden-badge-${job.id}`}>
                              <AlertCircle className="w-3 h-3 inline mr-1" />No photos
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-slate-400 mt-2">
                          <div>
                            <span className="text-slate-500">Worker:</span>{' '}
                            <span className="text-slate-200">{workerName || '—'}</span>
                          </div>
                          <div>
                            <span className="text-slate-500">Completed:</span>{' '}
                            <span className="text-slate-200">
                              {job.completed_at ? new Date(job.completed_at).toLocaleDateString() : '—'}
                            </span>
                          </div>
                          {duration && (
                            <div>
                              <span className="text-slate-500">Duration:</span>{' '}
                              <span className="text-slate-200">{duration}</span>
                            </div>
                          )}
                          {job.qb_synced_at && (
                            <div>
                              <span className="text-slate-500">Synced:</span>{' '}
                              <span className="text-slate-200">{new Date(job.qb_synced_at).toLocaleDateString()}</span>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
                <Pagination
                  total={invoicedJobs.length}
                  page={pages.invoiced}
                  pageSize={pageSize}
                  onPageChange={(p) => setPages((s) => ({ ...s, invoiced: p }))}
                  onPageSizeChange={setPageSize}
                  testId="invoiced-pagination"
                  itemLabel="job"
                />
              </div>
            )}
          </TabsContent>

          {/* ARCHIVE TAB - With Search */}
          <TabsContent value="archived" className="mt-6">
            {/* Archive Header with Auto-Archive Button */}
            {canManageJobs && isAdmin && (
              <div className="mb-4 flex justify-between items-center bg-[#0F1923] p-4 rounded-lg">
                <div>
                  <h3 className="font-semibold text-white">Archive Management</h3>
                  <p className="text-sm text-slate-400">View and manage archived jobs</p>
                </div>
                <Button
                  onClick={handleAutoArchive}
                  disabled={isLoading}
                  className="bg-gray-700 hover:bg-gray-800"
                >
                  <Clock className="w-4 h-4 mr-2" />
                  Auto-Archive (90+ days)
                </Button>
              </div>
            )}

            {/* Search Bar */}
            <Card className="mb-4 bg-[#1A2433]">
              <CardContent className="p-4">
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <Input
                      placeholder="🔍 Search jobs by title, customer, location..."
                      value={archiveSearch}
                      onChange={(e) => setArchiveSearch(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleArchiveSearch()}
                      className="flex-1"
                    />
                    <Button onClick={handleArchiveSearch} disabled={loadingArchive}>
                      Search
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => setShowArchiveFilters(!showArchiveFilters)}
                    >
                      Filters {showArchiveFilters ? '▲' : '▼'}
                    </Button>
                  </div>

                  {/* Advanced Filters */}
                  {showArchiveFilters && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                      <div>
                        <Label>Start Date</Label>
                        <Input
                          type="date"
                          value={archiveFilters.start_date}
                          onChange={(e) => setArchiveFilters({...archiveFilters, start_date: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>End Date</Label>
                        <Input
                          type="date"
                          value={archiveFilters.end_date}
                          onChange={(e) => setArchiveFilters({...archiveFilters, end_date: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Location</Label>
                        <Input
                          placeholder="City or address"
                          value={archiveFilters.location}
                          onChange={(e) => setArchiveFilters({...archiveFilters, location: e.target.value})}
                        />
                      </div>
                      <div className="flex items-end gap-2">
                        <Button onClick={handleArchiveSearch} className="flex-1">
                          Apply Filters
                        </Button>
                        <Button variant="outline" onClick={handleClearArchiveFilters}>
                          Clear
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Results Count */}
            {archivePagination.total_count > 0 && (
              <div className="mb-4 text-sm text-slate-400">
                Showing {archivedJobsList.length} of {archivePagination.total_count} archived jobs
              </div>
            )}

            {/* Spring checklist — seasonal-hold jobs with one-tap restore */}
            {canManageJobs && (
              <SeasonalRevivePanel token={token} toast={toast} onRestored={() => { fetchJobs(); fetchArchivedJobs(); }} />
            )}

            {/* Loading State */}
            {loadingArchive ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Loader2 className="w-12 h-12 mx-auto text-gray-400 mb-4 animate-spin" />
                  <p className="text-slate-500">Loading archived jobs...</p>
                </CardContent>
              </Card>
            ) : archivedJobsList.length === 0 ? (
              <Card className="border-2 border-dashed border-[#2D3748]">
                <CardContent className="p-12 text-center">
                  <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                  <p className="text-slate-500 text-lg font-medium">No archived jobs</p>
                  <p className="text-gray-400 text-sm mt-2">
                    {archiveSearch || archiveFilters.start_date || archiveFilters.end_date || archiveFilters.location
                      ? 'No jobs match your search criteria'
                      : 'Archived jobs will appear here'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Archived Jobs List */}
                <div className="space-y-4">
                  {archivedJobsList.map((job) => {
                    const duration = getJobDuration(job.started_at, job.completed_at);
                    const workerName = (job.is_team_job && job.team_name)
                    ? `Team · ${job.team_parent_name ? `${job.team_parent_name} › ${job.team_name}` : job.team_name}`
                    : ((job.crew_names && job.crew_names[job.assigned_to]) || getUserName(job.assigned_to));
                    
                    return (
                      <Card key={job.id} className="shadow-md border-l-4 border-[#2D3748] bg-[#1A2433]">
                        <CardContent className="p-5">
                          <div className="space-y-3">
                            {/* Header */}
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-2">
                                  <h3 className="text-lg font-bold text-white flex items-baseline gap-2 flex-wrap">{job.job_number && <span className="text-xs font-mono text-[#1E88E5] bg-[#1E88E5]/10 px-2 py-0.5 rounded" data-testid={`job-number-${job.id}`}>{job.job_number}</span>}<button type="button" onClick={(e) => { e.stopPropagation(); setDetailJob(job); }} className="text-left hover:text-blue-400 hover:underline transition-colors cursor-pointer" data-testid={`open-job-detail-${job.id}`} title="Open job details (notes, files, activity)">{job.title}</button>{unreadNotes[job.id] > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-500 text-white animate-pulse" title={`${unreadNotes[job.id]} unread note${unreadNotes[job.id] === 1 ? '' : 's'}`} data-testid={`unread-notes-badge-${job.id}`}>{unreadNotes[job.id]} new</span>}{canManageJobs && <button type="button" onClick={(e) => { e.stopPropagation(); setWorkbenchJob(job); }} className="text-slate-500 hover:text-[#1E88E5] transition-colors" data-testid={`open-workbench-${job.id}`} title="Job Workbench — crew, time and costs"><Wrench className="w-4 h-4" /></button>}</h3>
                                  <Badge className="bg-[#1A2433]0 text-white">📁 Archived</Badge>
                                  {job.status && job.status !== 'completed' && (
                                    <Badge variant="outline" className="border-[#FFA000] text-[#FFA000] text-[10px]" data-testid={`archived-status-${job.id}`}>{String(job.status).replace('_', ' ')} · restore to resume</Badge>
                                  )}
                                  {job.archive_reason && job.archive_reason !== 'unspecified' && (
                                    <Badge variant="outline" className="border-[#2D3748] text-slate-300 text-[10px]" data-testid={`archive-reason-${job.id}`}>{String(job.archive_reason).replace(/_/g, ' ')}</Badge>
                                  )}
                                  {job.deletion_reason && (
                                    <Badge variant="outline" className="border-red-500/50 text-red-300 text-[10px]">deleted · {String(job.deletion_reason).replace(/_/g, ' ')}</Badge>
                                  )}
                                </div>
                                {(job.label_ids || []).length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`job-labels-row-${job.id}`}>
                                  {(job.label_ids || []).map((lid) => labelsById[lid]).filter(Boolean).map((l) => (
                                    <LabelChip key={l.id} label={l} size="sm" />
                                  ))}
                                </div>
                              )}
                              {job.description && (
                                  <p className="text-sm text-slate-400 mb-2">{job.description}</p>
                                )}
                              </div>
                            </div>

                            {/* Job Details */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm bg-[#1A2433] p-3 rounded-lg">
                              <div className="flex items-center space-x-2 text-slate-300">
                                <User className="w-4 h-4 text-slate-400" />
                                <span className="font-medium">{job.status === 'completed' ? 'Completed by:' : 'Assigned to:'}</span>
                                <span>{workerName}</span>
                              </div>
                              <div className="flex items-center space-x-2 text-slate-300">
                                <Clock className="w-4 h-4 text-slate-400" />
                                <span className="font-medium">{job.status === 'completed' ? 'Duration:' : 'Hours so far:'}</span>
                                <span>{job.status === 'completed' ? duration : `${Number(job.total_hours_worked || 0).toFixed(2)} h`}</span>
                              </div>
                              <div className="flex items-center space-x-2 text-slate-300">
                                <CheckCircle2 className="w-4 h-4 text-slate-400" />
                                <span className="font-medium">{job.status === 'completed' ? 'Completed:' : 'Status:'}</span>
                                <span>{job.status === 'completed' ? formatDateTimeWithTimezone(job.completed_at, user?.timezone || 'America/Toronto') : `${String(job.status || '').replace('_', ' ')} — restore to continue`}</span>
                              </div>
                              <div className="flex items-center space-x-2 text-slate-300">
                                <FileText className="w-4 h-4 text-slate-400" />
                                <span className="font-medium">Archived:</span>
                                <span>{formatDateTimeWithTimezone(job.archived_at, user?.timezone || 'America/Toronto')}</span>
                              </div>
                              {job.customer_name && (
                                <div className="flex items-center space-x-2 text-slate-300">
                                  <User className="w-4 h-4 text-slate-400" />
                                  <span className="font-medium">Customer:</span>
                                  <span>{job.customer_name}</span>
                                </div>
                              )}
                              {job.job_address && (
                                <div className="flex items-center space-x-2 text-slate-300 col-span-2">
                                  <MapPin className="w-4 h-4 text-slate-400" />
                                  <span className="font-medium">Location:</span>
                                  <span className="truncate">{job.job_address}</span>
                                </div>
                              )}
                            </div>

                            {/* Photo Count */}
                            {job.photos && job.photos.length > 0 && (
                              <div className="flex items-center space-x-2 text-sm text-slate-400">
                                <Camera className="w-4 h-4" />
                                <span>{job.photos.length} photo{job.photos.length !== 1 ? 's' : ''} attached</span>
                              </div>
                            )}

                            {/* Actions */}
                            {canManageJobs && (
                              <div className="flex flex-wrap gap-2 pt-3 border-t">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleUnarchiveJob(job.id)}
                                  disabled={isLoading}
                                  className="border-[#2D3748] text-blue-700 hover:bg-[#0F1923]"
                                  data-testid={`restore-job-${job.id}`}
                                >
                                  <RefreshCw className="w-4 h-4 mr-1" />
                                  Restore
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setQuickNoteJob(job)}
                                  className="border-amber-500 text-amber-300 hover:bg-amber-500/10"
                                  data-testid={`add-note-job-${job.id}`}
                                  title="Add a note to this archived job"
                                >
                                  <StickyNote className="w-4 h-4 mr-1" />
                                  Add a Note
                                </Button>
                                {isAdmin && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleDeleteJob(job.id)}
                                    disabled={isLoading}
                                    className="border-red-300 text-red-700 hover:bg-[#0F1923]"
                                    data-testid={`delete-job-${job.id}`}
                                  >
                                    <Trash2 className="w-4 h-4 mr-1" />
                                    Delete Permanently
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* Pagination */}
                {archivePagination.total_pages > 1 && (
                  <div className="mt-6 flex justify-center items-center gap-4">
                    <Button
                      variant="outline"
                      onClick={() => setArchivePagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      disabled={!archivePagination.has_prev || loadingArchive}
                    >
                      Previous
                    </Button>
                    <span className="text-sm text-slate-400">
                      Page {archivePagination.page} of {archivePagination.total_pages}
                    </span>
                    <Button
                      variant="outline"
                      onClick={() => setArchivePagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      disabled={!archivePagination.has_next || loadingArchive}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>

        {/* Empty state (if no jobs at all) */}
        {jobs.length === 0 && (
          <Card className="shadow-lg">
            <CardContent className="p-12 text-center">
              <Briefcase className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <p className="text-slate-500 text-lg">No jobs {canManageJobs ? 'created' : 'assigned'} yet</p>
              {canManageJobs && (
                <Button
                  onClick={() => setShowAddDialog(true)}
                  className="mt-4 bg-[#1E88E5] hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Job
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Reassign / first-time-assign Dialog (Feb 2026)
          - Used for BOTH first-time assignment of estimate-converted jobs
            (where assigned_to is null) and standard reassignments.
          - Searchable input on user name/email/role so admins of larger
            companies can find a worker quickly.
          - Title + description switch based on whether the job already has
            an assignee so the dispatcher sees the right verb. */}
      <Dialog
        open={showReassignDialog}
        onOpenChange={(o) => {
          setShowReassignDialog(o);
          if (!o) { setReassignSearch(''); setReassignTargetType('user'); }
        }}
      >
        <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">
              {selectedJob?.assigned_to ? 'Reassign Job' : 'Assign Job'}
            </DialogTitle>
            <DialogDescription className="text-sm">
              {selectedJob?.assigned_to
                ? `Select a new assignee for "${selectedJob?.title}".`
                : `Pick the employee who should handle "${selectedJob?.title}". They'll get an email + SMS as soon as you assign it.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              {[['user', 'Worker'], ['team', 'Team']].map(([v, label]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setReassignTargetType(v)}
                  data-testid={`reassign-type-${v}`}
                  className="flex-1 rounded-lg px-3 py-2 text-sm font-semibold border"
                  style={{
                    background: reassignTargetType === v ? (v === 'team' ? '#A78BFA' : '#1E88E5') : '#0F1923',
                    color: reassignTargetType === v ? '#FFFFFF' : '#A0AEC0',
                    borderColor: reassignTargetType === v ? (v === 'team' ? '#A78BFA' : '#1E88E5') : '#2D3748',
                  }}
                >
                  {v === 'team' ? <Users className="w-4 h-4 inline mr-1.5" /> : <User className="w-4 h-4 inline mr-1.5" />}
                  {label}
                </button>
              ))}
            </div>
            <Label>Search</Label>
            <Input
              data-testid="reassign-search-input"
              autoFocus
              value={reassignSearch}
              onChange={(e) => setReassignSearch(e.target.value)}
              placeholder={reassignTargetType === 'team' ? 'Type a team name…' : 'Type a name, email, or role…'}
              className="h-10"
            />
            <Label className="pt-2">{reassignTargetType === 'team' ? 'Select team or sub-team' : 'Select assignee'}</Label>
            {reassignTargetType === 'team' ? (() => {
              const term = reassignSearch.trim().toLowerCase();
              const matches = teams.filter((t) => !term || (t.name || '').toLowerCase().includes(term));
              if (matches.length === 0) {
                return <p className="text-sm text-slate-500 py-3 text-center">No teams match &ldquo;{reassignSearch}&rdquo;.</p>;
              }
              return matches.map((t) => {
                const parent = t.parent_team_id ? teams.find((p) => p.id === t.parent_team_id) : null;
                return (
                  <Button
                    key={t.id}
                    data-testid={`reassign-pick-team-${t.id}`}
                    onClick={() => handleReassignJob(t.id, 'team')}
                    disabled={isLoading || t.id === selectedJob?.assigned_team_id}
                    variant="outline"
                    className="w-full justify-start h-12"
                  >
                    <Users className="w-4 h-4 mr-3 text-violet-400" />
                    <div className="text-left">
                      <p className="font-semibold">{parent ? `${parent.name} › ${t.name}` : t.name}</p>
                      <p className="text-xs text-slate-500">{parent ? 'Sub-team — every member gets the job' : 'Team — every member gets the job'}</p>
                    </div>
                  </Button>
                );
              });
            })() : (() => {
              const term = reassignSearch.trim().toLowerCase();
              const matches = users.filter((u) => {
                if (!term) return true;
                return (
                  (u.name || '').toLowerCase().includes(term)
                  || (u.email || '').toLowerCase().includes(term)
                  || (u.role || '').toLowerCase().includes(term)
                  || (u.user_type || '').toLowerCase().includes(term)
                );
              });
              if (matches.length === 0) {
                return (
                  <p className="text-sm text-slate-500 py-3 text-center">
                    No employees match &ldquo;{reassignSearch}&rdquo;.
                  </p>
                );
              }
              return matches.map((u) => (
                <Button
                  key={u.id}
                  data-testid={`reassign-pick-${u.id}`}
                  onClick={() => handleReassignJob(u.id)}
                  disabled={isLoading || u.id === selectedJob?.assigned_to}
                  variant="outline"
                  className="w-full justify-start h-12"
                >
                  <User className="w-4 h-4 mr-3" />
                  <div className="text-left">
                    <p className="font-semibold">{u.name}</p>
                    <p className="text-xs text-slate-500">{u.role} • {u.user_type}</p>
                  </div>
                </Button>
              ));
            })()}
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject Job Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl text-[#EF4444]">Reject Job</DialogTitle>
            <DialogDescription className="text-sm">
              Please provide a reason for rejecting "{selectedJob?.title}". Your manager will be notified.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="rejection_reason">Rejection Reason *</Label>
              <Textarea
                id="rejection_reason"
                placeholder="e.g., Schedule conflict, equipment not available, outside my expertise..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={4}
                className="resize-none"
                required
              />
            </div>
            <div className="flex space-x-3">
              <Button
                onClick={handleRejectJob}
                disabled={isLoading || !rejectionReason.trim()}
                className="flex-1 bg-red-600 hover:bg-red-700 h-11"
              >
                {isLoading ? 'Rejecting...' : 'Confirm Rejection'}
              </Button>
              <Button
                onClick={() => {
                  setShowRejectDialog(false);
                  setRejectionReason('');
                }}
                variant="outline"
                className="flex-1 h-11"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Upload Dialog */}
      {/* Job Detail dialog — Overview · Notes · Attachments · Activity (Jun 2026) */}
      <AdminJobWorkbench
        open={!!workbenchJob}
        onOpenChange={(v) => { if (!v) setWorkbenchJob(null); }}
        job={workbenchJob ? (jobs.find((j) => j.id === workbenchJob.id) || workbenchJob) : null}
        users={users}
        teams={teams}
        isAdmin={isAdmin}
        onUpdated={fetchJobs}
      />
      <JobDetailDialog
        job={detailJob}
        open={!!detailJob}
        onClose={() => { setDetailJob(null); fetchUnreadNotes(); fetchJobs(); }}
        currentUser={user}
        onMutated={() => { fetchUnreadNotes(); }}
      />

      <AdminPhotoDialog
        job={adminPhotoJob}
        open={!!adminPhotoJob}
        onClose={() => setAdminPhotoJob(null)}
        token={token}
        users={users}
        onUploaded={(photos) => {
          setAdminPhotoJob((j) => (j ? { ...j, photos: [...(j.photos || []), ...photos] } : j));
          fetchJobs();
        }}
      />

      {/* Admin-only: override the photo requirement to complete a job */}
      <Dialog open={photoOverrideModal.open} onOpenChange={(v) => !isLoading && setPhotoOverrideModal({ open: v, job: v ? photoOverrideModal.job : null })}>
        <DialogContent className="max-w-md" data-testid="photo-override-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-400" />
              Complete without photos?
            </DialogTitle>
            <DialogDescription>
              This job is set to require at least one photo, but none were uploaded.
              As an administrator you can override this and complete the job so it can move to billing.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm rounded-lg p-3 my-1" style={{ background: 'rgba(255,160,0,0.08)', border: '1px solid rgba(255,160,0,0.25)', color: '#C9A25A' }}>
            <span className="font-semibold">{photoOverrideModal.job?.title}</span> will be marked complete with no photos.
            This override is recorded on the job's activity log.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPhotoOverrideModal({ open: false, job: null })} disabled={isLoading} data-testid="photo-override-cancel">
              Cancel
            </Button>
            <Button
              onClick={() => { const j = photoOverrideModal.job; setPhotoOverrideModal({ open: false, job: null }); handleCompleteJob(j.id, true); }}
              disabled={isLoading}
              style={{ background: '#F0A020', color: '#0B1321' }}
              data-testid="photo-override-confirm"
            >
              {isLoading ? 'Completing…' : 'Override & Complete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Job Labels dialog (Feb 2026) */}
      <ManageLabelsDialog
        open={manageLabelsOpen}
        onOpenChange={setManageLabelsOpen}
        onChanged={() => { fetchLabels(); fetchJobs(); }}
      />

      {/* Safety attestation — gates Start. Opened from handleStartJob. */}
      <SafetyAttestationDialog
        open={!!safetyDialog.open}
        jobTitle={safetyDialog.jobTitle}
        isTeamJob={!!safetyDialog.isTeamJob}
        teamName={safetyDialog.teamName}
        onClose={() => setSafetyDialog({ open: false })}
        onAccept={async (template) => {
          const jobId = safetyDialog.jobId;
          setSafetyDialog({ open: false });
          await _doStartJob(jobId, template);
        }}
      />

      {/* Site QR scan — gates Start when the job has a posted QR code.
          Scanning verifies location (bypasses the GPS geofence); skipping
          falls back to the normal GPS flow. */}
      <QrScanDialog
        open={!!qrScanDialog.open}
        jobTitle={qrScanDialog.jobTitle}
        onClose={() => setQrScanDialog({ open: false })}
        onScan={async (tok) => {
          const { jobId, attestation } = qrScanDialog;
          setQrScanDialog({ open: false });
          await _doStartJob(jobId, attestation, null, tok);
        }}
        onSkip={async () => {
          const { jobId, attestation } = qrScanDialog;
          setQrScanDialog({ open: false });
          await _doStartJob(jobId, attestation, null, null);
        }}
      />

      {/* Bulk Site QR poster printing — one poster per page. */}
      <BulkQrPrintDialog open={bulkQrOpen} onClose={() => setBulkQrOpen(false)} jobs={jobs} />

      {/* Outside-geofence override — opened automatically when /start
          returns 403. Captures a reason then retries /start with the
          worker's existing GPS (so duration/route data stays honest). */}
      <OutsideGeofenceDialog
        open={!!geofenceDialog.open}
        jobTitle={geofenceDialog.jobTitle}
        distanceMeters={geofenceDialog.distanceMeters}
        radiusMeters={geofenceDialog.radiusMeters}
        onClose={() => setGeofenceDialog({ open: false })}
        onConfirm={async ({ reason, notes }) => {
          const { jobId, attestation, lat, lon } = geofenceDialog;
          setGeofenceDialog({ open: false });
          // Retry /start with the reason. We re-use the captured GPS so
          // the backend's auto-travel segment records the same position.
          setIsLoading(true);
          try {
            const body = {
              latitude: lat,
              longitude: lon,
              outside_geofence_reason: reason,
              ...(notes ? { outside_geofence_notes: notes } : {}),
            };
            if (attestation && attestation.accepted) {
              body.safety_attestation_accepted = true;
              body.safety_attestation_items = attestation.items;
              body.safety_attestation_version = attestation.version;
            }
            await axios.put(
              `${API}/jobs/${jobId}/start`,
              body,
              { headers: { Authorization: `Bearer ${token}` } },
            );
            toast({
              title: 'Travel Time started',
              description: 'Your clock is running as Travel Time. It will switch to On-site once you arrive.',
            });
            fetchJobs();
          } catch (err) {
            toast({
              title: 'Could not start job',
              description: err.response?.data?.detail || err.message,
              variant: 'destructive',
            });
          } finally {
            setIsLoading(false);
          }
        }}
      />


      <Dialog open={showPhotoDialog} onOpenChange={setShowPhotoDialog}>
        <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center space-x-2 text-lg sm:text-xl">
              <Camera className="w-5 h-5 sm:w-6 sm:h-6 text-[#1E88E5]" />
              <span>Upload Job Photos</span>
            </DialogTitle>
            <DialogDescription className="text-sm">
              {selectedJob?.photos_required && (
                <div className="flex items-center space-x-2 text-[#FFA000] font-semibold">
                  <AlertCircle className="w-4 h-4" />
                  <span>This job requires at least one photo</span>
                </div>
              )}
              Upload photos of the completed work — we'll auto-compress each to ≤5MB (up to 10 photos)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 flex-1 overflow-y-auto pr-2" data-testid="photo-dialog-scroll-area">
            {/* Current Photos */}
            {selectedJob?.photos && selectedJob.photos.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2 text-sm text-slate-300">
                  Uploaded Photos ({selectedJob.photos.length}/10)
                </h4>
                <div className="grid grid-cols-3 gap-2">
                  {selectedJob.photos.map((photo) => (
                    <div key={photo.id} className="relative group">
                      <PhotoImage
                        job={selectedJob}
                        photo={photo}
                        className="w-full h-24 object-cover rounded-lg border-2"
                      />
                      {(isAdmin || isSupervisor) && (
                        <Button
                          onClick={() => handleDeletePhoto(selectedJob.id, photo.id)}
                          variant="destructive"
                          size="sm"
                          className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Upload Area */}
            <div className="border-2 border-dashed border-[#2D3748] rounded-lg p-8 text-center hover:border-[#2D3748] transition-colors">
              <input
                type="file"
                id="photo-upload"
                accept="image/*"
                multiple
                onChange={(e) => handlePhotoUpload(e, selectedJob)}
                className="hidden"
                disabled={uploadingPhoto || (selectedJob?.photos?.length >= 10)}
              />
              <label htmlFor="photo-upload" className="cursor-pointer">
                <Upload className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                <p className="text-sm font-semibold text-slate-300">
                  Click to upload photos
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  JPG, PNG, or HEIC — auto-compressed to ≤5MB
                </p>
              </label>
              {uploadingPhoto && (
                <div className="mt-4 space-y-2" data-testid="photo-upload-progress">
                  {Object.keys(photoUploadProgress).length === 0 ? (
                    <div className="flex items-center justify-center space-x-2">
                      <Loader2 className="w-5 h-5 animate-spin text-[#1E88E5]" />
                      <span className="text-sm text-[#1E88E5]">Uploading...</span>
                    </div>
                  ) : (
                    Object.entries(photoUploadProgress).map(([name, pct]) => (
                      <div key={name} className="text-left">
                        <div className="flex justify-between text-xs text-slate-400 mb-0.5">
                          <span className="truncate max-w-[70%]">{name}</span>
                          <span>{pct}%</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-[#2D3748] overflow-hidden">
                          <div className="h-full bg-[#1E88E5] rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3">
              <Button
                onClick={() => {
                  setShowPhotoDialog(false);
                  if (selectedJob) {
                    handleCompleteJob(selectedJob.id);
                  }
                }}
                disabled={uploadingPhoto || (selectedJob?.photos_required && (!selectedJob?.photos || selectedJob.photos.length === 0))}
                className="flex-1 bg-[#00C896] hover:bg-green-700"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Complete Job
              </Button>
              <Button
                onClick={() => setShowPhotoDialog(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Gallery Dialog */}
      <Dialog open={showPhotoGallery} onOpenChange={setShowPhotoGallery}>
        <DialogContent className="max-w-[95vw] sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2 text-lg sm:text-xl">
              <Image className="w-5 h-5 sm:w-6 sm:h-6 text-[#1E88E5]" />
              <span className="truncate">Job Photos - {selectedJob?.title}</span>
            </DialogTitle>
          </DialogHeader>
          
          {selectedJob?.photos && selectedJob.photos.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-h-[500px] overflow-y-auto">
              {selectedJob.photos.map((photo, idx) => (
                <div key={photo.id} className="min-w-0">
                  <div className="relative group">
                    <PhotoImage
                      job={selectedJob}
                      photo={photo}
                      className="w-full h-48 object-cover rounded-lg border-2 cursor-zoom-in hover:border-[#2D3748]"
                      onClick={() => {
                        // Open the in-app lightbox at this image (no more
                        // window.open new-tab — lightbox keeps users in context
                        // and adds keyboard/prev/next nav).
                        const images = selectedJob.photos.map((p) => ({
                          src: buildPhotoUrl(selectedJob.id, p.filename),
                          name: photoDisplayName(p),
                          id: p.id,
                        }));
                        setPhotoLightbox({ images, index: idx });
                      }}
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white p-2 rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity">
                      <p className="text-xs truncate">{photo.original_filename}</p>
                      <p className="text-xs text-gray-300">By {photo.uploaded_by_name}</p>
                      <p className="text-xs text-gray-300">{new Date(photo.uploaded_at).toLocaleDateString()}</p>
                    </div>
                    {(isAdmin || isSupervisor) && (
                      <Button
                        onClick={() => handleDeletePhoto(selectedJob.id, photo.id)}
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <PhotoMetaBar
                    jobId={selectedJob.id}
                    photo={photo}
                    onUpdated={(updated) => {
                      const mapPhotos = (j) => ({
                        ...j,
                        photos: (j.photos || []).map((p) => (p.id === updated.id ? { ...p, ...updated } : p)),
                      });
                      setSelectedJob((j) => (j && j.id === selectedJob.id ? mapPhotos(j) : j));
                      setJobs((list) => list.map((j) => (j.id === selectedJob.id ? mapPhotos(j) : j)));
                    }}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Camera className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-slate-500">No photos uploaded for this job</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Photos Dialog */}
      <EmailJobPhotosDialog
        open={showEmailPhotosDialog}
        onOpenChange={setShowEmailPhotosDialog}
        selectedJob={selectedJob}
        emailRecipient={emailRecipient}
        setEmailRecipient={setEmailRecipient}
        sendingEmail={sendingEmail}
        setSendingEmail={setSendingEmail}
        token={token}
        toast={toast}
      />

      <EmailJobReportDialog
        open={showEmailReportDialog}
        onOpenChange={setShowEmailReportDialog}
        selectedJob={selectedJob}
        emailRecipient={emailRecipient}
        setEmailRecipient={setEmailRecipient}
        includePhotosInReport={includePhotosInReport}
        setIncludePhotosInReport={setIncludePhotosInReport}
        sendingEmail={sendingEmail}
        setSendingEmail={setSendingEmail}
        token={token}
        toast={toast}
      />

      {/* QuickBooks preflight (test sync) */}
      <QBTestSyncDialog
        open={!!testSyncJob}
        onOpenChange={(o) => { if (!o) setTestSyncJob(null); }}
        job={testSyncJob}
        token={token}
      />

      {/* Email PDF invoice to customer */}
      <Dialog open={!!emailInvoiceJob} onOpenChange={(o) => { if (!o) { setEmailInvoiceJob(null); setEmailInvoiceTo(''); setEmailInvoiceMessage(''); setIncludePhotoReport(true); setIncludePhotosZip(false); } }}>
        <DialogContent className="max-w-md bg-[#0F1923] border border-[#2D3748] text-white" data-testid="email-invoice-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" style={{ color: '#1E88E5' }} />
              Email invoice
            </DialogTitle>
            <DialogDescription style={{ color: '#94A3B8' }}>
              We'll generate a branded PDF for{' '}
              <span className="text-white font-medium">{emailInvoiceJob?.title}</span> and email it to
              the customer with your invoice template's reply-to address.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-xs font-medium block mb-1" style={{ color: '#94A3B8' }}>To</label>
              <input
                type="email"
                placeholder={emailInvoiceJob?.customer_email || 'customer@example.com'}
                value={emailInvoiceTo}
                onChange={(e) => setEmailInvoiceTo(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm bg-[#0B1321] border border-[#2D3748] text-white focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                data-testid="email-invoice-to"
              />
              <p className="text-[11px] mt-1" style={{ color: '#64748B' }}>
                Leave blank to use the customer's saved email.
              </p>
            </div>
            <div>
              <label className="text-xs font-medium block mb-1" style={{ color: '#94A3B8' }}>Message (optional)</label>
              <textarea
                rows={3}
                value={emailInvoiceMessage}
                onChange={(e) => setEmailInvoiceMessage(e.target.value)}
                placeholder="Add a short note for the customer."
                className="w-full px-3 py-2 rounded-lg text-sm bg-[#0B1321] border border-[#2D3748] text-white focus:outline-none focus:ring-2 focus:ring-blue-500/40 resize-none"
                data-testid="email-invoice-message"
                maxLength={500}
              />
            </div>
            {/* Photo attachments (Feb 2026) — only show when the job actually
                has photos so we don't tease an option that does nothing. */}
            {(emailInvoiceJob?.photos || []).length > 0 && (
              <div className="rounded-lg p-3 space-y-2" style={{ background: 'rgba(0,200,150,0.05)', border: '1px solid rgba(0,200,150,0.18)' }}>
                <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#00C896' }}>
                  Include job photos ({(emailInvoiceJob.photos || []).length})
                </p>
                <label className="flex items-start gap-2 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    checked={includePhotoReport}
                    onChange={(e) => setIncludePhotoReport(e.target.checked)}
                    className="mt-0.5"
                    data-testid="email-invoice-include-photo-report"
                  />
                  <span style={{ color: '#E2E8F0' }}>
                    <span className="font-medium">Photo Report PDF</span>
                    <span className="block text-[11px]" style={{ color: '#94A3B8' }}>
                      Printable 2-column report with captions — recommended for invoicing.
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    checked={includePhotosZip}
                    onChange={(e) => setIncludePhotosZip(e.target.checked)}
                    className="mt-0.5"
                    data-testid="email-invoice-include-photos-zip"
                  />
                  <span style={{ color: '#E2E8F0' }}>
                    <span className="font-medium">All photos as a ZIP</span>
                    <span className="block text-[11px]" style={{ color: '#94A3B8' }}>
                      Original files. Useful when the customer wants the raw images.
                    </span>
                  </span>
                </label>
              </div>
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => { setEmailInvoiceJob(null); setEmailInvoiceTo(''); setEmailInvoiceMessage(''); setIncludePhotoReport(true); setIncludePhotosZip(false); }} data-testid="email-invoice-cancel">
              Cancel
            </Button>
            <Button
              onClick={handleSendInvoiceEmail}
              disabled={emailInvoiceSending}
              className="bg-[#1E88E5] text-white hover:bg-[#1976D2]"
              data-testid="email-invoice-send"
            >
              {emailInvoiceSending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
              {emailInvoiceSending ? 'Sending…' : 'Send invoice'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Reassign Dialog */}
      <Dialog open={showBulkReassignDialog} onOpenChange={(o) => { if (!bulkReassigning) setShowBulkReassignDialog(o); }}>
        <DialogContent data-testid="jobs-bulk-reassign-dialog">
          <DialogHeader>
            <DialogTitle>Reassign {selectedJobIds.size} job{selectedJobIds.size !== 1 ? 's' : ''}</DialogTitle>
            <DialogDescription>
              Choose a single worker or a team to receive all selected jobs. Completed and archived jobs will be skipped automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Type toggle */}
            <div className="flex gap-2" role="radiogroup" aria-label="Reassign target type">
              <button
                type="button"
                onClick={() => { setBulkAssigneeType('user'); setBulkAssigneeId(''); }}
                className="flex-1 rounded-lg px-3 py-2 text-sm font-semibold border"
                style={{
                  background: bulkAssigneeType === 'user' ? '#1E88E5' : '#0F1923',
                  color: bulkAssigneeType === 'user' ? '#FFFFFF' : '#A0AEC0',
                  borderColor: bulkAssigneeType === 'user' ? '#1E88E5' : '#2D3748',
                }}
                data-testid="bulk-reassign-type-user"
              >
                <User className="w-4 h-4 inline mr-1.5" /> Worker
              </button>
              <button
                type="button"
                onClick={() => { setBulkAssigneeType('team'); setBulkAssigneeId(''); }}
                className="flex-1 rounded-lg px-3 py-2 text-sm font-semibold border"
                style={{
                  background: bulkAssigneeType === 'team' ? '#A78BFA' : '#0F1923',
                  color: bulkAssigneeType === 'team' ? '#FFFFFF' : '#A0AEC0',
                  borderColor: bulkAssigneeType === 'team' ? '#A78BFA' : '#2D3748',
                }}
                data-testid="bulk-reassign-type-team"
              >
                <Users className="w-4 h-4 inline mr-1.5" /> Team
              </button>
            </div>

            <div>
              <Label htmlFor="bulk-reassign-target">
                {bulkAssigneeType === 'user' ? 'Assign to worker' : 'Assign to team'}
              </Label>
              <select
                id="bulk-reassign-target"
                value={bulkAssigneeId}
                onChange={(e) => setBulkAssigneeId(e.target.value)}
                className="mt-1 w-full rounded-lg px-3 py-2 outline-none"
                style={{ background: '#0F1923', color: '#FFFFFF', border: '1px solid #2D3748' }}
                data-testid="bulk-reassign-target-select"
              >
                <option value="">— Select {bulkAssigneeType} —</option>
                {bulkAssigneeType === 'user'
                  ? users.filter((u) => u.role !== 'admin' || u.id === user.id).map((u) => (
                      <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
                    ))
                  : teams.map((t) => {
                      const parent = t.parent_team_id ? teams.find((p) => p.id === t.parent_team_id) : null;
                      return <option key={t.id} value={t.id}>{parent ? `${parent.name} › ${t.name}` : t.name}</option>;
                    })}
              </select>
              {bulkAssigneeType === 'team' && teams.length === 0 && (
                <p className="text-xs mt-1.5 text-slate-400">No teams in your company yet — create one in <strong>Staff → Manage Teams</strong>.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkReassignDialog(false)} disabled={bulkReassigning}>
              Cancel
            </Button>
            <Button
              onClick={handleBulkReassign}
              disabled={bulkReassigning || !bulkAssigneeId || selectedJobIds.size === 0}
              className="bg-[#1E88E5] hover:bg-blue-700 text-white"
              data-testid="bulk-reassign-confirm-btn"
            >
              {bulkReassigning ? 'Reassigning…' : `Reassign ${selectedJobIds.size} job${selectedJobIds.size !== 1 ? 's' : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Invoice Editor dialog (Feb 2026) — replaces the old greyed-button flow ──
          Admin sets rate, materials, tax override, memo and chooses to send to QB,
          download PDF, or email PDF. Always usable on completed jobs. */}
      <InvoiceEditorDialog
        open={!!invoiceJob}
        job={invoiceJob}
        token={token}
        onClose={() => { setInvoiceJob(null); setInvoiceResult(null); }}
        onInvoiced={() => { fetchJobs(); }}
      />

      {/* ── Bulk Generate Invoices dialog ── */}
      <Dialog
        open={bulkInvoiceOpen}
        onOpenChange={(o) => {
          if (!o) {
            setBulkInvoiceOpen(false);
            setBulkInvoiceResult(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-[560px]" data-testid="bulk-invoice-dialog">
          {!bulkInvoiceResult ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Send className="w-5 h-5" style={{ color: '#2CA01C' }} />
                  Bulk Generate QuickBooks Invoices
                </DialogTitle>
                <DialogDescription>
                  Push invoices to QuickBooks Online for the {selectedJobIds.size} selected completed
                  job{selectedJobIds.size !== 1 ? 's' : ''}. Jobs missing a billing rate or hours will be skipped automatically.
                </DialogDescription>
              </DialogHeader>
              {(() => {
                const selected = completedJobs.filter(j => selectedJobIds.has(j.id) && !j.qb_invoice_id);
                const eligible = selected.filter(j => Number(j.service_rate || 0) > 0 && Number(j.total_hours_worked || 0) > 0);
                const totalAmount = eligible.reduce((sum, j) => sum + Number(j.service_rate || 0) * Number(j.total_hours_worked || 0), 0);
                const skipped = selected.length - eligible.length;
                return (
                  <div className="rounded-lg border" style={{ background: '#0F1923', borderColor: '#2D3748' }} data-testid="bulk-invoice-summary">
                    <div className="grid grid-cols-3 px-4 py-3 text-sm">
                      <div>
                        <div className="text-xs uppercase tracking-wider" style={{ color: '#A0AEC0' }}>Eligible</div>
                        <div className="text-white text-xl font-semibold" data-testid="bulk-eligible-count">{eligible.length}</div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wider" style={{ color: '#A0AEC0' }}>Will be skipped</div>
                        <div className="text-amber-400 text-xl font-semibold" data-testid="bulk-skipped-count">{skipped}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs uppercase tracking-wider" style={{ color: '#A0AEC0' }}>Total to invoice</div>
                        <div className="text-xl font-bold" style={{ color: '#2CA01C' }} data-testid="bulk-invoice-total">
                          {formatCurrency(totalAmount)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
              <DialogFooter>
                <Button variant="ghost" onClick={() => setBulkInvoiceOpen(false)} disabled={bulkInvoiceRunning} data-testid="bulk-invoice-cancel-btn">
                  Cancel
                </Button>
                <Button
                  onClick={handleBulkGenerateInvoices}
                  disabled={bulkInvoiceRunning}
                  className="text-white"
                  style={{ background: '#2CA01C' }}
                  data-testid="bulk-invoice-confirm-btn"
                >
                  {bulkInvoiceRunning
                    ? (<><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Sending to QuickBooks…</>)
                    : (<><Send className="w-4 h-4 mr-1.5" />Generate Invoices</>)}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" style={{ color: '#00C896' }} />
                  Bulk invoicing complete
                </DialogTitle>
                <DialogDescription>{bulkInvoiceResult.message}</DialogDescription>
              </DialogHeader>
              {(() => {
                const totalInvoicedToday = (bulkInvoiceResult.results || [])
                  .filter(r => r.status === 'synced')
                  .reduce((sum, r) => {
                    const job = completedJobs.find(j => j.id === r.job_id);
                    if (!job) return sum;
                    return sum + Number(job.service_rate || 0) * Number(job.total_hours_worked || 0);
                  }, 0);
                const syncedCount = (bulkInvoiceResult.results || []).filter(r => r.status === 'synced').length;
                return totalInvoicedToday > 0 ? (
                  <div
                    className="rounded-lg px-4 py-3 flex items-center justify-between"
                    style={{ background: 'linear-gradient(90deg, rgba(44,160,28,0.18), rgba(0,200,150,0.12))', border: '1px solid rgba(44,160,28,0.45)' }}
                    data-testid="bulk-invoice-total-today"
                  >
                    <div>
                      <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: '#A0AEC0' }}>
                        Total invoiced today
                      </div>
                      <div className="text-xs" style={{ color: '#A0AEC0' }}>
                        {syncedCount} invoice{syncedCount !== 1 ? 's' : ''} pushed to QuickBooks Online
                      </div>
                    </div>
                    <div className="text-2xl font-bold" style={{ color: '#2CA01C' }} data-testid="bulk-invoice-total-today-amount">
                      {formatCurrency(totalInvoicedToday)}
                    </div>
                  </div>
                ) : null;
              })()}
              <div className="rounded-lg border max-h-[280px] overflow-y-auto" style={{ background: '#0F1923', borderColor: '#2D3748' }} data-testid="bulk-invoice-result">
                {(bulkInvoiceResult.results || []).map((r) => {
                  const job = completedJobs.find(j => j.id === r.job_id);
                  const label = job?.job_number || (r.job_id || '').slice(0, 8);
                  const isOk = r.status === 'synced';
                  const isSkip = r.status === 'already_synced';
                  return (
                    <div key={r.job_id} className="flex items-center justify-between px-4 py-2 border-b text-sm" style={{ borderColor: '#2D3748' }}>
                      <div className="flex-1 min-w-0">
                        <div className="text-white font-mono text-xs">{label}</div>
                        {job?.title && <div className="text-xs truncate" style={{ color: '#A0AEC0' }}>{job.title}</div>}
                      </div>
                      <div className="flex items-center gap-2 ml-3 shrink-0">
                        {isOk && r.invoice_id && qboInvoiceUrl(r.invoice_id) && (
                          <a
                            href={qboInvoiceUrl(r.invoice_id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs underline"
                            style={{ color: '#2CA01C' }}
                          >
                            #{String(r.invoice_id).slice(0, 8)}
                          </a>
                        )}
                        <span
                          className="text-xs font-semibold px-2 py-0.5 rounded"
                          style={{
                            background: isOk ? 'rgba(0,200,150,0.15)' : isSkip ? 'rgba(255,160,0,0.15)' : 'rgba(239,68,68,0.15)',
                            color: isOk ? '#00C896' : isSkip ? '#FFA000' : '#EF4444',
                          }}
                        >
                          {isOk ? 'Synced' : isSkip ? 'Already synced' : (r.error || 'Failed')}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <DialogFooter>
                <Button onClick={() => { setBulkInvoiceOpen(false); setBulkInvoiceResult(null); }} data-testid="bulk-invoice-done-btn">
                  Done
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Pause Job Modal */}
      {pauseModal.open && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center p-4"
          style={{ background: 'rgba(11,19,33,0.8)', backdropFilter: 'blur(4px)' }}
          data-testid="pause-job-modal"
        >
          <div
            className="w-full max-w-md rounded-2xl p-6"
            style={{ background: '#1A2433', border: '1px solid #2D3748' }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,160,0,0.15)' }}>
                <Pause className="w-5 h-5" style={{ color: '#FFA000' }} />
              </div>
              <h3 className="text-lg font-semibold text-white">Pause Job</h3>
            </div>
            <p className="text-sm mb-4" style={{ color: '#A0AEC0' }}>
              Pausing will end this work session. You can resume the job later.
              The assigned supervisor and company admins will be notified.
            </p>

            <label className="block text-sm font-medium text-white mb-1">
              Reason <span className="text-red-400">*</span>
            </label>
            <select
              value={pauseModal.reason}
              onChange={(e) => setPauseModal((p) => ({ ...p, reason: e.target.value }))}
              data-testid="pause-reason-select"
              className="w-full mb-4 px-3 py-2 rounded-lg text-white text-sm"
              style={{ background: '#0F1923', border: '1px solid #2D3748' }}
            >
              <option value="">Select a reason…</option>
              <option value="Waiting for parts">Waiting for parts</option>
              <option value="Waiting for subcontractor">Waiting for subcontractor</option>
              <option value="Weather delay">Weather delay</option>
              <option value="Client requested pause">Client requested pause</option>
              <option value="Other">Other</option>
            </select>

            <label className="block text-sm font-medium text-white mb-1">
              Additional details (optional)
            </label>
            <textarea
              value={pauseModal.notes}
              onChange={(e) => setPauseModal((p) => ({ ...p, notes: e.target.value }))}
              rows={3}
              placeholder="e.g., Parts ordered, expected Friday"
              data-testid="pause-notes-input"
              className="w-full mb-5 px-3 py-2 rounded-lg text-white text-sm resize-none"
              style={{ background: '#0F1923', border: '1px solid #2D3748' }}
            />

            <div className="flex gap-3">
              <button
                onClick={closePauseModal}
                disabled={isLoading}
                className="flex-1 py-2 rounded-lg font-medium"
                style={{ background: '#2D3748', color: 'white' }}
                data-testid="pause-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handlePauseJob}
                disabled={isLoading || !pauseModal.reason}
                className="flex-1 py-2 rounded-lg font-medium disabled:opacity-60"
                style={{ background: '#FFA000', color: '#0B1321' }}
                data-testid="pause-confirm-btn"
              >
                {isLoading ? 'Pausing…' : 'Pause Job'}
              </button>
            </div>
          </div>
        </div>
      )}

      </div>  {/* Close Content wrapper */}

      {photoLightbox && (
        <Lightbox
          images={photoLightbox.images}
          index={photoLightbox.index}
          onIndexChange={(i) => setPhotoLightbox((lb) => (lb ? { ...lb, index: i } : lb))}
          onClose={() => setPhotoLightbox(null)}
        />
      )}

      <ArchiveJobDialog
        key={archiveJobTarget?.id || 'none'}
        job={archiveJobTarget}
        busy={isLoading}
        onClose={() => setArchiveJobTarget(null)}
        onConfirm={performArchiveJob}
      />

      {/* Soft-delete + permanent-delete confirmation with archive reason */}
      <DeleteWithReasonDialog
        open={!!deleteJobTarget}
        onClose={() => setDeleteJobTarget(null)}
        title={deleteJobTarget?.archived
          ? `Permanently delete "${deleteJobTarget?.title || 'this job'}"?`
          : `Move "${deleteJobTarget?.title || 'this job'}" to Archive`}
        description={deleteJobTarget?.archived
          ? 'This job is already archived. Erasing it removes all photos, notes, and history forever.'
          : `${deleteJobTarget?.status === 'in_progress' ? 'This job is IN PROGRESS — deleting it stops the clock for everyone on it. ' : ''}The job will be soft-deleted and appear on the Archive page. Admins can restore it later if this was a mistake.`}
        entityLabel="job"
        onConfirm={performDeleteJob}
        dangerLevel={deleteJobTarget?.archived ? 'permanent' : 'soft'}
      />

      {/* Per-job quick-note (notepad-style). Always available regardless of
          job status — Feb 2026 ask from production workers (janitors, HVAC,
          plumbers, etc.) who need to capture info while on the job. */}
      <QuickJobNoteDialog
        open={!!quickNoteJob}
        onClose={() => setQuickNoteJob(null)}
        job={quickNoteJob}
        token={token}
        currentUser={user}
        onSaved={() => {
          // Refresh unread-notes counters + jobs list so the new note is
          // visible to teammates immediately (no full page reload).
          fetchJobs();
        }}
      />

      {/* Bulk photo ZIP download — admins/supervisors (Jun 2026) */}
      <BulkPhotoDownloadDialog
        open={bulkPhotoOpen}
        onOpenChange={setBulkPhotoOpen}
        api={API}
        token={token}
      />

    </div>
  );
};

export default JobsEnhanced;
